import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { pricing as localPricing, stateMultiplier, TVA_TAUX, MARGE_COORDINATION, COORDINATION_TAUX } from '../../data/pricing';
import RoomCanvas, { polygonArea, polygonPerimeter, polylineLength, rectVertices } from '../../components/pro/RoomCanvas';
import PhotoCapture from '../../components/pro/PhotoCapture';
import AfterStudio from '../../components/pro/AfterStudio';
import StyleStudio from '../../components/pro/StyleStudio';
import AddressAutocomplete from '../../components/pro/AddressAutocomplete';
import { proFetch, proHeaders } from '../../utils/proApi';
import { Page, EnTetePage } from '../../components/pro/ui';
import EstimatifClient, { aUneDemande, titreDemande, resumeDemande } from '../../components/pro/EstimatifClient';
import BASES from '../../data/bases.json';
import { construireDepuisDossier, empreinteDe, positionSelonEtat, prixSelonEtat, sensibiliteEtat, niveauDuNom, ORDRE_NIVEAUX, ENSEMBLE, categorieLieu, placerPhotos, estNomGenerique } from '../../utils/releveDepuisDossier';
import { logementsDepuisPieces, reseauxDepuisLogements, nettoyerLignes, poserReseaux, LIGNES_REMPLACEES, RESEAUX_PIECE, COMMUNS, PIECE_TECHNIQUE } from '../../utils/reseaux';
import LogementsReseaux from '../../components/pro/LogementsReseaux';
import PlanNiveaux, { niveauDe, regrouperParNiveau } from '../../components/pro/PlanNiveaux';
import StyleSuggestion from '../../components/pro/StyleSuggestion';
import PhotosPresentation from '../../components/pro/PhotosPresentation';
import ReleveDeposeur from '../../components/pro/ReleveDeposeur';
import DossierVisite from '../../components/pro/DossierVisite';
import { getPendingBlob, deletePending } from '../../utils/photoQueue';

// ─── Constants ────────────────────────────────────────────────────────────────

const WALL_HEIGHT = 2.5;

const ROOM_TYPES = [
  { id: 'sejour',  label: 'Séjour / Salon',   icon: '🛋️' },
  { id: 'cuisine', label: 'Cuisine',           icon: '🍳' },
  { id: 'chambre', label: 'Chambre',           icon: '🛏️' },
  { id: 'sdb',     label: 'Salle de bain',     icon: '🚿' },
  { id: 'wc',      label: 'WC séparé',         icon: '🚽' },
  { id: 'couloir', label: 'Couloir / Entrée',  icon: '🚪' },
  { id: 'facade',  label: 'Façade / Extérieur', icon: '🏗️' },
  { id: 'autre',   label: 'Autre pièce',       icon: '📦' },
];

// All pricing items flattened for the pro interface — staff can add ANY item to ANY room
function getAllItems(pricing) {
  const items = [];
  for (const [cat, entries] of Object.entries(pricing)) {
    for (const [id, data] of Object.entries(entries)) {
      if (data.obsolete) continue;   // retiré de la grille : lisible dans un vieux devis, plus proposé
      items.push({
        key: `${cat}.${id}`,
        category: cat,
        id,
        ...data,
      });
    }
  }
  return items;
}

// ─── Postes compagnons ────────────────────────────────────────────────────────
// Un poste en appelle un autre qu'on oublie facilement (12/10/2026 : plus de
// niveau de difficulté — une règle vaut ou ne vaut pas).
const SUGGESTION_RULES = [
  { trigger: 'sols.carrelage_douche_sol', suggest: ['sols.membrane_etancheite'], reason: 'Membrane d\'étanchéité obligatoire sous carrelage douche' },
  { trigger: 'demolition.demolition_cloison_np', suggest: ['demolition.evacuation_gravats'], reason: 'Prévoir évacuation des gravats' },
  { trigger: 'sols.carrelage_600', suggest: ['sols.depose_ancien_sol'], reason: 'Dépose ancien revêtement avant pose' },
  { trigger: 'sols.parquet_stratifie', suggest: ['sols.depose_ancien_sol'], reason: 'Dépose ancien revêtement avant pose' },
  { trigger: 'sols.sol_pvc', suggest: ['sols.depose_ancien_sol'], reason: 'Dépose ancien revêtement avant pose' },
  { trigger: 'murs.cloison_placo_std', suggest: ['preparation.bande_joint_placo'], reason: 'Bandes à joint nécessaires après pose placo' },
  { trigger: 'murs.cloison_placo_acou', suggest: ['preparation.bande_joint_placo'], reason: 'Bandes à joint nécessaires après pose placo' },
  { trigger: 'facade.peinture_facade', suggest: ['ite.ite_echafaudage'], reason: 'Échafaudage nécessaire pour travaux en hauteur' },
  { trigger: 'ite.ite_laine_roche', suggest: ['ite.ite_echafaudage', 'ite.ite_profil_depart'], reason: 'Échafaudage et profilé de départ nécessaires' },
];

// Postes m² facturés à la surface de MURS (périmètre × hauteur) quand la quantité
// est laissée en auto (qty=1) — tout autre poste m² utilise la surface au sol.
const WALL_AREA_IDS = new Set(BASES.murs);   // la même liste que le chiffrage serveur (bases.json)

// ─── Brouillon local (secours hors-ligne) ────────────────────────────────────
// Chaque sauvegarde écrit aussi le relevé dans localStorage. Si le serveur est
// injoignable au chargement (ou si le brouillon local est plus récent que la
// version serveur), on repart du brouillon et la boucle de resync le pousse
// dès que le réseau revient.

const draftKey = (ref) => `pro_draft:${ref}`;

function readDraft(ref) {
  try { return JSON.parse(localStorage.getItem(draftKey(ref))); } catch { return null; }
}

// Jamais de data-URL photo dans le brouillon (quota localStorage ~5 Mo) —
// les photos uploadées gardent id/url S3 ; celles prises hors-ligne gardent
// leur pendingId (le blob est en IndexedDB, envoyé au retour du réseau)
function slimReleve(data) {
  const slimPhoto = (p) => (p && (p.id || p.url || p.pendingId))
    ? { id: p.id, url: p.url, pendingId: p.pendingId, caption: p.caption || '', timestamp: p.timestamp || '' }
    : null;
  return {
    ...data,
    projectPhotos: (data.projectPhotos || []).map(slimPhoto).filter(Boolean),
    rooms: (data.rooms || []).map(r => ({ ...r, photos: (r.photos || []).map(slimPhoto).filter(Boolean) })),
  };
}

function writeDraft(ref, data, synced) {
  try {
    localStorage.setItem(draftKey(ref), JSON.stringify({ savedAt: Date.now(), synced, data: slimReleve(data) }));
  } catch { /* quota plein — le serveur reste la source de vérité */ }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Une pièce aux dimensions non relevées (3 × 3 provisoires). Une pièce
// « Installations techniques » n'en est pas une : elle n'a pas de surface.
function estPieceProvisoire(r) {
  if (!r || r.technique) return false;
  return !!r.provisoire || /provisoire|non coté/i.test(r.notes || '');
}

function resolveItem(pricing, key) {
  const [cat, id] = key.split('.');
  return pricing?.[cat]?.[id] || null;
}

// Le maxi de grille, retenu d'au plus 5 % selon la position (état du bien pondéré par poste).
function effectivePrice(item, pos) {
  return prixSelonEtat(item, pos);
}

function fmt(n) {
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProDevis() {
  // `ref` is kept in state (not just the route param) so a brand-new devis
  // (/pro/devis/new) can be promoted to a real lead on the fly without remount.
  const { ref: paramRef } = useParams();
  const [ref, setRef] = useState(paramRef);
  const refRef = React.useRef(paramRef);
  React.useEffect(() => { refRef.current = ref; }, [ref]);
  // Sync when navigating between existing leads (route param changes, no remount)
  React.useEffect(() => { if (paramRef && paramRef !== ref) setRef(paramRef); }, [paramRef]); // eslint-disable-line react-hooks/exhaustive-deps
  // pin is still needed for sessionStorage check in ProLayout

  // ── State ─────────────────────────────────────────────────────────────────
  const [pricing, setPricing] = useState(localPricing);
  const [loading, setLoading] = useState(!!ref);
  const [lead, setLead] = useState(null);

  // Client info
  const [client, setClient] = useState({
    prenom: '', nom: '', email: '', telephone: '', adresse: '', ville: '', lat: null, lng: null,
    siege: '', siren: '', tvaIntra: '',   // client professionnel
  });

  // Auto-save client info to lead (debounced 2s)
  // Only track non-address fields for the debounce to avoid killing Google autocomplete dropdown
  const clientSaveTimerRef = React.useRef(null);
  const clientSaveKey = `${client.prenom}|${client.nom}|${client.email}|${client.telephone}|${client.siege}|${client.siren}|${client.tvaIntra}`;
  React.useEffect(() => {
    if (!ref) return;
    if (clientSaveTimerRef.current) clearTimeout(clientSaveTimerRef.current);
    clientSaveTimerRef.current = setTimeout(() => {
      if (client.prenom || client.nom || client.email) {
        proFetch(`/leads/${ref}/client`, { method: 'PATCH', body: JSON.stringify(client) }).catch(() => {});
      }
    }, 2000);
    return () => clearTimeout(clientSaveTimerRef.current);
  }, [ref, clientSaveKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save address immediately when autocomplete selects (lat/lng change)
  React.useEffect(() => {
    if (!ref || !client.lat) return;
    proFetch(`/leads/${ref}/client`, { method: 'PATCH', body: JSON.stringify({ adresse: client.adresse, ville: client.ville, lat: client.lat, lng: client.lng }) }).catch(() => {});
  }, [ref, client.lat, client.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Property
  const [state, setState] = useState('bon_etat');
  const [tvaKey, setTvaKey] = useState('tva_10');
  const [coordKey, setCoordKey] = useState('coord_3');

  // Style global du projet (palette appliquée aux rendus avant/après IA)
  // { wallColor:{label,hex,prompt}, floorTone:{label,prompt}, lighting:{label,prompt}, ambiance:{label,prompt} }
  const [style, setStyle] = useState({ wallColor: null, floorTone: null, lighting: null, ambiance: null });
  const [clientBrief, setClientBrief] = useState(''); // style décrit par le client (audio) → consignes prompt IA
  // Titre du projet sur la couverture du dossier (« Extension du centre de santé
  // dentaire ») : vide, il se déduit du type de bien.
  const [titre, setTitre] = useState('');

  // Rooms
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);

  // Logements & réseaux : les lots techniques se chiffrent par logement
  // (src/utils/reseaux.js), pas au point ni au mètre — enregistrés avec le relevé.
  const [logements, setLogements] = useState([]);
  const [modeChauffage, setModeChauffage] = useState('pac');
  const [reseauxOuvert, setReseauxOuvert] = useState(false);
  // Pièce aux dimensions provisoires dont on demande de saisir ou d'assumer l'estimation.
  const [aAssumer, setAAssumer] = useState(null);
  // Pièces validées pendant cette session : seules elles partent d'elles-mêmes au
  // rendu — rouvrir un ancien devis ne relance pas une série d'images.
  const [valideesIci, setValideesIci] = useState(() => new Set());

  // Site-wide photos (façade, accès, compteurs…) + survey persistence guard
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [showProjectPhotos, setShowProjectPhotos] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Lazily promote a brand-new devis to a real lead (ref) the first time we need
  // to persist anything (photo / survey), so /pro/devis/new can save too.
  const createPromiseRef = React.useRef(null);
  const ensureRef = useCallback(async () => {
    if (refRef.current) return refRef.current;
    if (createPromiseRef.current) return createPromiseRef.current;
    createPromiseRef.current = (async () => {
      const res = await proFetch('/leads', { method: 'POST', body: JSON.stringify({}) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ref) throw new Error(data.error || 'Création du devis impossible');
      refRef.current = data.ref;
      setRef(data.ref);
      // Update the URL in place — react-router navigation would remount and drop state
      window.history.replaceState(null, '', `/pro/devis/${data.ref}`);
      return data.ref;
    })();
    try { return await createPromiseRef.current; }
    catch (e) { createPromiseRef.current = null; throw e; }
  }, []);

  // Upload a compressed photo blob to S3 via the backend → { id, url }
  const uploadPhotoToS3 = useCallback(async (blob) => {
    const r = await ensureRef();
    const fd = new FormData();
    fd.append('photo', blob, `photo-${Date.now()}.jpg`);
    const res = await fetch(`/api/pro/leads/${r}/photos`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('pro_token') || ''}`,
      },
      body: fd,
    });
    if (!res.ok) throw new Error('Envoi de la photo échoué');
    return res.json();
  }, [ensureRef]);

  const [sendError, setSendError] = useState('');
  const [quoteId, setQuoteId] = useState(null);

  // Send to client
  const [sendingClient, setSendingClient] = useState(false);
  const [sentClient, setSentClient] = useState(false);

  // Team suggestion
  const [nbOuvriers, setNbOuvriers] = useState(2);
  const [urgence, setUrgence] = useState('normal');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [teamSuggestions, setTeamSuggestions] = useState(null);

  // ── Load pricing + lead ───────────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/pricing')
      .then(r => r.json())
      .then(data => { if (data) setPricing(data); })
      .catch(() => {});
  }, []);

  // Les clés que le chiffrage saura tarifer : celles du barème local et
  // celles du référentiel serveur (postes ajoutés, repères web).
  const clesConnues = async () => {
    const connues = new Set(getAllItems(localPricing).map(i => i.key));
    try { const pr = await fetch('/api/pricing').then(r => (r.ok ? r.json() : null)); if (pr) getAllItems(pr).forEach(i => connues.add(i.key)); } catch { /* le local suffit */ }
    return connues;
  };

  // Construit le relevé depuis le dossier de visite et le pose dans le wizard.
  // Appelé au chargement quand rien n'a été enregistré, et par le bandeau
  // quand le dossier a été ré-analysé après un relevé retouché.
  // La grille du serveur, lue une fois (repli : le barème local).
  const grilleRef = React.useRef(null);
  const grilleCourante = async () => {
    if (grilleRef.current) return grilleRef.current;
    try { const pr = await fetch('/api/pricing').then(r => (r.ok ? r.json() : null)); if (pr) grilleRef.current = pr; } catch { /* repli local */ }
    return grilleRef.current || localPricing;
  };
  // Ce que la préparation automatique a fait, montré une fois à l'étape Pièces.
  const [preparation, setPreparation] = useState(null);
  const reprendreDepuisDossier = async (data) => {
    if (!data?.dossier) return false;
    console.info('[relevé] reprise depuis le dossier', data.dossier.analyseLe);
    const r = construireDepuisDossier(data, {
      connues: await clesConnues(), bases: BASES, roomTypes: ROOM_TYPES, wallHeight: WALL_HEIGHT, rectVertices, stateMultiplier,
    });
    if (!r) return false;
    // Une pièce déjà connue garde ce qu'on lui avait donné : rendu « après »
    // et consignes, validation, cloisons tracées, photos ajoutées à la main.
    const anciennes = data.releve?.rooms || [];
    const cle = (x) => (x.customLabel || x.label || '').trim().toLowerCase();
    const pieces = r.rooms.map(nr => {
      const av = anciennes.find(a => cle(a) === cle(nr));
      if (!av) return nr;
      // Une photo du dossier ré-uploadée sous un autre id à une ré-analyse est la
      // même image : on la reconnaît à son nom de fichier, jamais deux fois.
      const ids = new Set((nr.photos || []).map(ph => ph.id));
      const noms = new Set((nr.photos || []).map(ph => ph.caption).filter(Boolean));
      return {
        ...nr,
        afterPhoto: av.afterPhoto || null, aiBrief: av.aiBrief, valide: !!av.valide,
        walls: (av.walls || []).length ? av.walls : nr.walls,
        photos: [...(nr.photos || []), ...(av.photos || []).filter(ph => ph.id && !ids.has(ph.id) && !(ph.caption && noms.has(ph.caption)))],
      };
    });
    // Ce que le métreur n'a plus à faire : doublons et travaux Enedis retirés,
    // logements et lots techniques posés quand le relevé le justifie.
    const grille = await grilleCourante();
    const item = (k) => { const [c, id] = k.split('.'); return grille?.[c]?.[id] || null; };
    const net = nettoyerLignes(pieces, { regles: BASES, uniteDe: k => item(k)?.unit || '', libelleDe: k => item(k)?.label || '' });
    const pose = poserReseaux(net.rooms, { regles: BASES, modeChauffage, niveauDe, ORDRE_NIVEAUX, polygonArea, existe: k => !!item(k), rectVertices, wallHeight: WALL_HEIGHT });
    r.rooms = pose.rooms;
    setLogements(pose.logements);
    setPreparation({ exclus: net.exclus, retirees: net.retirees, reseaux: pose.genere });
    retoucheRef.current = false;
    console.info('[relevé] reprise faite :', r.rooms.length, 'pièces,', r.rooms.flatMap(x => x.lines).length, 'lignes ; retirées', net.retirees.length, '; exclus', net.exclus.length, '; réseaux', pose.genere);
    setRooms(r.rooms);
    setActiveRoomId(r.rooms[0]?.id || null);
    setProjectPhotos(r.projectPhotos);
    if (r.etat) setState(r.etat);
    if (r.tvaCle) setTvaKey(r.tvaCle);
    setDepuisDossier({ analyseLe: data.dossier.analyseLe || null, empreinte: empreinteDe(r.rooms) });
    setRepriseDossier({ date: data.dossier.analyseLe || data.dossierUpdatedAt || null, sansCotes: r.sansCotes });
    setDossierPlusRecent(false);
    return true;
  };

  useEffect(() => {
    if (!ref) { setLoading(false); setHydrated(true); return; }
    // Applique un relevé (serveur ou brouillon local) à l'état du wizard
    const applyReleve = (r, dossier) => {
      if (!r?.rooms?.length) return false;
      // Une photo ne vit que dans une pièce. Les relevés construits avant le
      // 27/09 au soir répétaient une photo dans plusieurs pièces : on la garde
      // dans celle dont la catégorie (cave, garage, cuisine…) et l'étage
      // correspondent à ce que l'inspection y a vu, sinon dans la première.
      const obs = dossier?.observationsPhotos || [];
      const obsDe = (p) => obs.find(o => o.nom === p.caption);
      const lieuDe = (p) => obsDe(p)?.lieu || '';
      const pieceDe = (p) => obsDe(p)?.piece || null;   // la pièce désignée par l'arbitre, quand il y en a une
      const catDe = (room) => categorieLieu(BASES, room.customLabel || room.label || '');
      const nivDe = (room) => (room.niveau && (niveauDuNom(room.niveau) || room.niveau)) || niveauDuNom(room.customLabel || room.label) || null;
      const porteurs = new Map();   // id photo → pièces qui l'ont
      r.rooms.forEach((room, i) => (room.photos || []).forEach(p => { if (p?.id) (porteurs.get(p.id) || porteurs.set(p.id, []).get(p.id)).push(i); }));
      const gardien = new Map();
      for (const [id, idx] of porteurs) {
        if (idx.length === 1) { gardien.set(id, idx[0]); continue; }
        const p = r.rooms[idx[0]].photos.find(x => x.id === id);
        const lieu = lieuDe(p); const cat = categorieLieu(BASES, lieu); const niv = niveauDuNom(lieu);
        const parCat = idx.filter(i => cat && catDe(r.rooms[i]) === cat);
        const parCatNiv = parCat.filter(i => !niv || nivDe(r.rooms[i]) === niv);
        gardien.set(id, (parCatNiv[0] ?? parCat[0] ?? idx[0]));
      }
      let rooms = r.rooms.map((room, i) => ({ ...room, photos: (room.photos || []).filter(p => !p?.id || gardien.get(p.id) === i) }));
      // Dans une pièce, deux photos du même nom de fichier sont la même image
      // (ré-upload à une ré-analyse) : on garde celle que le dossier connaît.
      const idsDossier = new Set((dossier?.photos || []).map(p => p.id));
      rooms = rooms.map(room => {
        const vues = new Map();
        for (const p of room.photos) { const k = p?.caption || p?.id; if (!k) continue; const prev = vues.get(k); if (!prev || (idsDossier.has(p.id) && !idsDossier.has(prev.id))) vues.set(k, p); }
        return { ...room, photos: room.photos.filter(p => !p?.caption || vues.get(p.caption) === p) };
      });
      const retirees = r.rooms.reduce((n, room) => n + (room.photos || []).length, 0) - rooms.reduce((n, room) => n + room.photos.length, 0);
      if (retirees) console.info('[relevé] photos en double retirées :', retirees);
      // Et une photo rangée dans une pièce d'une autre catégorie que ce que
      // l'inspection y a vu (une cave dans le débarras du 1er) rejoint la
      // pièce de sa catégorie, à l'étage écrit s'il l'est, quand elle existe.
      let deplacees = 0;
      const cible = (cat, niv) => rooms.findIndex(rm => catDe(rm) === cat && (!niv || nivDe(rm) === niv)) ?? -1;
      rooms = rooms.map(rm => ({ ...rm, photos: [...rm.photos] }));
      rooms.forEach((rm, i) => {
        for (const p of [...rm.photos]) {
          if (!p?.id) continue;
          const piece = pieceDe(p);
          let j = -1;
          if (piece) {
            j = rooms.findIndex(rm2 => (rm2.customLabel || rm2.label) === piece);
            if (j < 0 || j === i) continue;
          } else {
            const lieu = lieuDe(p); const cat = categorieLieu(BASES, lieu);
            if (!cat || catDe(rm) === cat) continue;
            j = cible(cat, niveauDuNom(lieu)); if (j < 0) j = cible(cat, null);
            if (j < 0 || j === i) continue;
          }
          rm.photos = rm.photos.filter(x => x !== p); rooms[j].photos.push(p); deplacees++;
        }
      });
      if (deplacees) console.info('[relevé] photos rangées dans la pièce de leur catégorie :', deplacees);
      // Une photo du dossier qu'aucune pièce ne porte (situées « Chambre » alors
      // que la cote disait « Pièce 1 ») trouve sa pièce, et une pièce au nom
      // générique prend le nom que ses photos lui donnent. Neuf photos chargées,
      // une seule proposée en rendu : les six autres attendaient dans le projet.
      const tenues = new Set(rooms.flatMap(rm => rm.photos.map(p => p.id)));
      const orphelines = (dossier?.photos || []).filter(p => p.id && !tenues.has(p.id)).map(p => ({ id: p.id, url: `/api/photo/${p.id}`, src: `/api/photo/${p.id}`, caption: p.nom || '' }));
      let projet = Array.isArray(r.projectPhotos) ? r.projectPhotos : [];
      if (orphelines.length && obs.length) {
        const pieces = rooms.map(rm => { const nom = rm.customLabel || rm.label; return { nom, niveau: nivDe(rm), texte: [nom, rm.notes].filter(Boolean).join(' '), nbPhotos: rm.photos.length, generique: estNomGenerique(BASES, nom) && !/provisoire|non coté/i.test(rm.notes || '') }; });
        const { par, renommages } = placerPhotos(orphelines, obs, pieces, (t) => categorieLieu(BASES, t));
        let posees = 0;
        rooms = rooms.map(rm => {
          const nom = rm.customLabel || rm.label;
          const siennes = orphelines.filter(p => par.get(p.id) === nom);
          // Seul un relevé construit depuis le dossier se renomme d'après ses
          // photos ; un relevé saisi garde les noms qu'on lui a donnés.
          const nouveau = r.depuisDossier ? renommages.get(nom) : null;
          if (!siennes.length && !nouveau) return rm;
          posees += siennes.length;
          return {
            ...rm, photos: [...rm.photos, ...siennes],
            ...(nouveau ? { customLabel: nouveau, notes: [`Nommée d'après ses photos (cote saisie « ${nom} »)`, rm.notes].filter(Boolean).join(' · ') } : {}),
          };
        });
        if (posees) { console.info('[relevé] photos orphelines rangées :', posees, renommages.size ? `— pièces nommées : ${[...renommages].map(([a, b]) => `${a} → ${b}`).join(', ')}` : ''); projet = projet.filter(p => !par.has(p.id)); }
      }
      setRooms(rooms);
      if (r.state) setState(r.state);
      if (Array.isArray(r.logements)) setLogements(r.logements);
      if (r.modeChauffage) setModeChauffage(r.modeChauffage);
      if (r.tvaKey) setTvaKey(r.tvaKey);
      if (r.coordKey && COORDINATION_TAUX[r.coordKey]) setCoordKey(r.coordKey);
      if (r.style) setStyle(r.style);
      if (r.clientBrief) setClientBrief(r.clientBrief);
      if (r.titre) setTitre(r.titre);
      if (Array.isArray(r.projectPhotos)) setProjectPhotos(projet);
      setActiveRoomId(rooms[0]?.id || null);
      return true;
    };
    proFetch(`/leads/${ref}`)
      .then(r => r.ok ? r.json() : Promise.reject('Not found'))
      .then(async data => {
        setLead(data);
        // Pre-fill client
        if (data.client) {
          setClient(prev => ({
            prenom: data.client.prenom || prev.prenom,
            nom: data.client.nom || prev.nom,
            email: data.client.email || prev.email,
            telephone: data.client.telephone || prev.telephone,
            adresse: data.client.adresse || prev.adresse,
            ville: prev.ville,
            siege: data.client.siege || prev.siege,
            siren: data.client.siren || prev.siren,
            tvaIntra: data.client.tvaIntra || prev.tvaIntra,
          }));
        }
        // Brouillon local plus récent que le serveur (saisie hors-ligne jamais
        // poussée) → il prime, et la boucle de resync le renvoie au serveur
        const draft = readDraft(ref);
        const serverTs = data.releveUpdatedAt ? new Date(data.releveUpdatedAt).getTime() : 0;
        if (draft && !draft.synced && draft.savedAt > serverTs && applyReleve(draft.data, data.dossier)) {
          setRestoredFromDraft(true);
          setSaveState('error'); // déclenche le réessai automatique
          return;
        }
        // Le dossier de visite du Deviseur (pièces cotées, travaux, photos)
        // devient le relevé — cf. utils/releveDepuisDossier. Un relevé
        // enregistré prime, sauf s'il avait été construit ainsi, jamais
        // retouché, et que le dossier a été ré-analysé depuis : alors il suit.
        // Un relevé retouché n'est jamais remplacé sans qu'on le demande
        // (bandeau « Reprendre le relevé depuis le dossier »).
        const dossierExploitable = !!(data.dossier?.releve && (data.dossier.releve.zones?.length
          || (data.dossier.observationsPhotos || []).some(o => !o.plan && o.lieu)));
        // Seule une nouvelle analyse compte : un recalcul de prix côté serveur
        // ne change ni les pièces ni les lignes de la page, qui chiffre
        // elle-même au référentiel. (Compter le recalcul faisait revenir le
        // bandeau sur un relevé retouché à chaque « recalculer ».)
        const tDossier = data.dossier?.analyseLe ? new Date(data.dossier.analyseLe).getTime() : 0;
        const tReleve = data.releveUpdatedAt ? new Date(data.releveUpdatedAt).getTime() : 0;
        const derive = data.releve?.depuisDossier || null;
        const intact = !!derive && (derive.retouche != null ? !derive.retouche : derive.empreinte === empreinteDe(data.releve.rooms));
        // Un relevé construit depuis le dossier se compare à l'analyse dont il
        // vient, pas à sa dernière sauvegarde : la page enregistre à chaque
        // ouverture, ce qui le ferait toujours passer pour plus récent.
        const tBase = derive?.analyseLe ? new Date(derive.analyseLe).getTime() : tReleve;
        const plusRecent = dossierExploitable && tDossier > tBase;
        console.info('[relevé] chargement :', { rooms: data.releve?.rooms?.length || 0, derive, intact, plusRecent, tDossier: new Date(tDossier).toISOString(), tBase: new Date(tBase).toISOString() });
        // Une analyse que l'on vient de relancer depuis cette page est ce que
        // l'on veut voir : le relevé se reconstruit, retouché ou non (les rendus,
        // consignes, validations et cloisons des pièces de même nom sont repris).
        // Avant, la page rechargeait sur l'ancien relevé avec un simple bandeau.
        let reanalyse = false;
        try { reanalyse = sessionStorage.getItem(`pro_reanalyse:${ref}`) === '1'; if (reanalyse) sessionStorage.removeItem(`pro_reanalyse:${ref}`); } catch {}
        let repris = false;
        if (data.releve?.rooms?.length && !(intact && plusRecent) && !(reanalyse && dossierExploitable && plusRecent)) {
          applyReleve(data.releve, data.dossier);
          if (derive) { setDepuisDossier(derive); retoucheRef.current = !!derive.retouche; }
          if (plusRecent) setDossierPlusRecent(true);
        } else if (dossierExploitable) {
          repris = await reprendreDepuisDossier(data);
        }

        // Pre-fill rooms from client estimate (only if there is no saved survey yet)
        const srcRooms = data.devis?.rooms || data.context?.rooms;
        if (!repris && !data.releve?.rooms?.length && srcRooms?.length && rooms.length === 0) {
          const converted = srcRooms.map((sr, i) => {
            // Match room type from name
            const typeName = (sr.name || '').toLowerCase();
            const matchedType = ROOM_TYPES.find(t =>
              typeName.includes(t.label.toLowerCase().split(' ')[0]) ||
              typeName.includes(t.id)
            ) || ROOM_TYPES[ROOM_TYPES.length - 1];

            // Derive dimensions from surface (assume ~4:3 ratio)
            const surface = parseFloat(sr.surface) || 12;
            const ratio = 4 / 3;
            const w = Math.sqrt(surface / ratio);
            const l = w * ratio;

            // Try to match line labels back to pricing keys
            const allPricingItems = getAllItems(localPricing);
            const nonRepris = [];
            const lines = (sr.lines || []).map(sl => {
              // Match by label similarity
              const match = allPricingItems.find(pi =>
                pi.label.toLowerCase().includes(sl.label.toLowerCase().split('(')[0].trim().toLowerCase()) ||
                sl.label.toLowerCase().includes(pi.label.toLowerCase().split('(')[0].trim().toLowerCase())
              );
              // Un poste que le référentiel ne reconnaît pas disparaissait sans
              // bruit du relevé : il part en note, à traiter pendant la visite.
              if (!match) { nonRepris.push(`${sl.label} (${sl.detail})`); return null; }
              return { key: match.key, qty: quantiteDemandee(sl.detail), priceOverride: null };
            }).filter(Boolean);

            const note = [
              sr.subtotal ? `Estimatif client : ${sr.subtotal}` : '',
              nonRepris.length ? `Demandé en ligne, non repris automatiquement : ${nonRepris.join(' ; ')}` : '',
            ].filter(Boolean).join('\n');

            return {
              id: Date.now() + i,
              type: matchedType.id,
              label: matchedType.label,
              customLabel: sr.name !== matchedType.label ? sr.name : '',
              vertices: rectVertices(parseFloat(l.toFixed(1)), parseFloat(w.toFixed(1))),
              walls: [],
              height: String(WALL_HEIGHT),
              lines,
              addons: [],
              photos: [],
              notes: note,
            };
          });
          setRooms(converted);
          if (converted.length > 0) setActiveRoomId(converted[0].id);
        }
      })
      .catch(() => {
        // Serveur injoignable (chantier sans réseau) : repartir du brouillon
        // local — la boucle de resync poussera dès que le réseau revient
        const draft = readDraft(ref);
        if (applyReleve(draft?.data)) {
          setRestoredFromDraft(true);
          setSaveState('error');
        }
      })
      .finally(() => { setLoading(false); setHydrated(true); });
  }, [ref]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-save the Pro survey (debounced) — only after initial hydration so we
  //    never overwrite a stored relevé with the empty initial state.
  //    L'état d'enregistrement est visible (barre du bas) et les échecs sont
  //    réessayés automatiquement (10 s + retour du réseau) : plus de perte
  //    silencieuse de relevé sur un chantier sans signal. ──
  const releveSaveRef = React.useRef(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [demandeOuverte, setDemandeOuverte] = useState(false);
  const [repriseDossier, setRepriseDossier] = useState(null);   // date du dossier de visite repris
  // Relevé construit depuis le dossier : de quelle analyse, et l'empreinte des
  // pièces à ce moment-là — enregistrés avec lui, pour savoir plus tard s'il a
  // été retouché. Et le signal qu'une analyse plus récente attend.
  const [depuisDossier, setDepuisDossier] = useState(null);
  const [dossierPlusRecent, setDossierPlusRecent] = useState(false);
  // Écran Plans : la pièce cliquée s'ouvre en grand, photo du plan à côté du
  // contour à corriger. Écran Postes : les pièces défilent une à une.
  const [planOuvert, setPlanOuvert] = useState(false);
  // Le relevé a-t-il été retouché à la main depuis qu'il a été construit depuis
  // le dossier ? Posé par chaque geste (contour, ligne, pièce, photo, note),
  // remis à zéro par une reprise. Enregistré avec le relevé : c'est lui qui
  // décide si une nouvelle analyse remplace le relevé sans demander.
  const retoucheRef = React.useRef(false);
  const marquerRetouche = (quoi = '') => {
    if (!retoucheRef.current) console.info('[relevé] retouche :', quoi || (new Error().stack || '').split('\n')[2]?.trim());
    retoucheRef.current = true;
  };
  const releveDataRef = React.useRef(null);
  releveDataRef.current = { rooms, state, logements, modeChauffage, tvaKey, coordKey, projectPhotos, style, clientBrief, titre, depuisDossier: depuisDossier ? { ...depuisDossier, retouche: retoucheRef.current } : null };

  const saveReleve = useCallback(async () => {
    if (!refRef.current) return;
    // Les anciennes lignes portaient une difficulté : elle ne sert plus, on ne la réécrit pas.
    const brut = releveDataRef.current;
    const payload = { ...brut, rooms: (brut.rooms || []).map(r => ({ ...r, lines: (r.lines || []).map(({ difficulty, ...l }) => l) })) };
    writeDraft(refRef.current, payload, false); // filet local avant toute tentative réseau
    setSaveState('saving');
    try {
      const res = await proFetch(`/leads/${refRef.current}/releve`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      writeDraft(refRef.current, payload, true);
      setSaveState('saved');
      setLastSavedAt(new Date());
    } catch {
      setSaveState('error');
    }
  }, []);

  React.useEffect(() => {
    if (!ref || !hydrated) return;
    if (releveSaveRef.current) clearTimeout(releveSaveRef.current);
    releveSaveRef.current = setTimeout(saveReleve, 1500);
    return () => clearTimeout(releveSaveRef.current);
  }, [ref, hydrated, rooms, state, logements, modeChauffage, tvaKey, coordKey, projectPhotos, style, clientBrief, titre, depuisDossier, saveReleve]);

  // Échec → réessai toutes les 10 s et dès que le réseau revient
  React.useEffect(() => {
    if (saveState !== 'error') return;
    const t = setTimeout(saveReleve, 10000);
    window.addEventListener('online', saveReleve);
    return () => { clearTimeout(t); window.removeEventListener('online', saveReleve); };
  }, [saveState, saveReleve]);

  // Garde-fou : prévenir avant de quitter avec un relevé non enregistré
  React.useEffect(() => {
    const handler = (e) => {
      if (saveState === 'saving' || saveState === 'error') { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  // ── Envoi différé des photos prises hors-ligne (blobs en IndexedDB) ──
  // Dès que le réseau est prouvé (relevé sauvé / event online), chaque photo
  // en attente est uploadée sur S3 puis remplacée par son {id, url} définitif.
  const flushingRef = React.useRef(false);
  const flushPendingPhotos = useCallback(async () => {
    if (flushingRef.current || !refRef.current) return;
    flushingRef.current = true;
    try {
      const { rooms: rs, projectPhotos: pps } = releveDataRef.current;
      const pendingIds = [
        ...rs.flatMap(r => (r.photos || []).map(p => p.pendingId).filter(Boolean)),
        ...(pps || []).map(p => p.pendingId).filter(Boolean),
      ];
      for (const pid of pendingIds) {
        const blob = await getPendingBlob(pid);
        if (!blob) continue; // blob perdu (autre appareil ?) — le marqueur reste visible
        const { id, url } = await uploadPhotoToS3(blob); // jette si toujours hors-ligne
        const swap = (p) => (p.pendingId === pid
          ? { id, url, caption: p.caption || '', timestamp: p.timestamp || '' }
          : p);
        setRooms(prev => prev.map(r => ({ ...r, photos: (r.photos || []).map(swap) })));
        setProjectPhotos(prev => prev.map(swap));
        await deletePending(pid);
      }
    } catch { /* réseau toujours absent — on retentera au prochain signal */ }
    finally { flushingRef.current = false; }
  }, [uploadPhotoToS3]);

  React.useEffect(() => {
    if (saveState === 'saved') flushPendingPhotos();
  }, [saveState, flushPendingPhotos]);
  React.useEffect(() => {
    window.addEventListener('online', flushPendingPhotos);
    return () => window.removeEventListener('online', flushPendingPhotos);
  }, [flushPendingPhotos]);

  // ── All pricing items ─────────────────────────────────────────────────────
  const allItems = useMemo(() => getAllItems(pricing), [pricing]);

  // ── Room management ───────────────────────────────────────────────────────
  const addRoom = (typeId, niveau = null) => {
    marquerRetouche();
    const type = ROOM_TYPES.find(t => t.id === typeId) || ROOM_TYPES[ROOM_TYPES.length - 1];
    const room = {
      id: Date.now(),
      type: type.id,
      label: type.label,
      customLabel: '',
      // Outline polygon (meters) — empty: the user draws the contour first
      vertices: [],
      walls: [],    // interior partitions (cloisons): { id, points:[{x,y}], kind:'existing'|'create'|'demolish' }
      height: String(WALL_HEIGHT),
      lines: [],    // { key, qty, priceOverride, note? }
      addons: [],   // { label, unit, price, qty }
      photos: [],   // { src, caption, timestamp }
      notes: '',
      niveau,       // étage, pour lire le bien niveau par niveau
    };
    setRooms(prev => [...prev, room]);
    setActiveRoomId(room.id);
    ensureRef().catch(() => {}); // provision a lead so the survey persists (new devis)
  };

  const updateRoom = useCallback((roomId, updates) => {
    setRooms(prev => prev.map(r => {
      if (r.id !== roomId) return r;
      // Un composant qui renvoie ce qu'il a reçu (le canevas au montage) n'est
      // pas une retouche — même s'il rend la hauteur en nombre là où elle
      // était enregistrée en texte : « 2.88 » et 2.88 sont la même hauteur.
      const memeValeur = (a, b) => JSON.stringify(a, normaliser) === JSON.stringify(b, normaliser);
      const changes = Object.keys(updates).filter(k => !memeValeur(updates[k], r[k]));
      if (!changes.length) return r;
      marquerRetouche(`updateRoom ${r.customLabel || r.label} : ${changes.join(', ')}`);
      return { ...r, ...updates };
    }));
  }, []);

  const removeRoom = (roomId) => {
    marquerRetouche();
    setRooms(prev => prev.filter(r => r.id !== roomId));
    if (activeRoomId === roomId) setActiveRoomId(null);
  };

  // Suppression en deux taps (pas de confirm() natif — inutilisable au doigt)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const confirmDeleteTimer = React.useRef(null);
  const requestRemoveRoom = (roomId) => {
    if (confirmDeleteId === roomId) {
      clearTimeout(confirmDeleteTimer.current);
      setConfirmDeleteId(null);
      removeRoom(roomId);
      return;
    }
    setConfirmDeleteId(roomId);
    clearTimeout(confirmDeleteTimer.current);
    confirmDeleteTimer.current = setTimeout(() => setConfirmDeleteId(null), 3500);
  };

  const duplicateRoom = (roomId) => {
    marquerRetouche();
    setRooms(prev => {
      const source = prev.find(r => r.id === roomId);
      if (!source) return prev;
      const copy = { ...source, id: Date.now(), label: source.label + ' (copie)', lines: [...source.lines.map(l => ({ ...l }))], addons: [...source.addons.map(a => ({ ...a }))] };
      return [...prev, copy];
    });
  };

  const addLine = (roomId, key) => {
    marquerRetouche();
    updateRoom(roomId, {
      lines: [...(rooms.find(r => r.id === roomId)?.lines || []), { key, qty: 1, priceOverride: null }],
    });
  };

  // Un poste a le même prix dans tout le devis : un PU forcé dans une pièce
  // vaut pour ce poste partout. Sinon la même peinture serait à 22 ici et à
  // 25 là.
  const [propagation, setPropagation] = useState(null);
  const updateLine = (roomId, lineIdx, updates) => {
    marquerRetouche();
    setRooms(prev => prev.map(r => {
      if (r.id !== roomId) return r;
      const lines = [...r.lines];
      lines[lineIdx] = { ...lines[lineIdx], ...updates };
      return { ...r, lines };
    }));
    // Un PU forcé vaut pour ce poste dans tout le devis : on le dit.
    if ('priceOverride' in updates && updates.priceOverride != null) {
      const cle = rooms.find(r => r.id === roomId)?.lines[lineIdx]?.key;
      const autres = rooms.filter(r => r.id !== roomId && r.lines.some(l => l.key === cle)).length;
      setPropagation(autres ? { texte: `Ce prix vaut pour ce poste dans tout le devis (${autres} autre${autres > 1 ? 's' : ''} pièce${autres > 1 ? 's' : ''}).` } : null);
    }
  };
  React.useEffect(() => {
    if (!propagation) return undefined;
    const t = setTimeout(() => setPropagation(null), 4000);
    return () => clearTimeout(t);
  }, [propagation]);

  const removeLine = (roomId, lineIdx) => {
    marquerRetouche();
    setRooms(prev => prev.map(r => {
      if (r.id !== roomId) return r;
      return { ...r, lines: r.lines.filter((_, i) => i !== lineIdx) };
    }));
  };

  // Add-on management

  const addCustomAddon = (roomId) => {
    marquerRetouche();
    setRooms(prev => prev.map(r => {
      if (r.id !== roomId) return r;
      return { ...r, addons: [...r.addons, { label: '', unit: 'm²', price: 0, qty: 1 }] };
    }));
  };

  const updateAddon = (roomId, addonIdx, updates) => {
    marquerRetouche();
    setRooms(prev => prev.map(r => {
      if (r.id !== roomId) return r;
      const addons = [...r.addons];
      addons[addonIdx] = { ...addons[addonIdx], ...updates };
      return { ...r, addons };
    }));
  };

  const removeAddon = (roomId, addonIdx) => {
    marquerRetouche();
    setRooms(prev => prev.map(r => {
      if (r.id !== roomId) return r;
      return { ...r, addons: r.addons.filter((_, i) => i !== addonIdx) };
    }));
  };

  // ── Estimation ────────────────────────────────────────────────────────────
  const pos = stateMultiplier[state]?.pos ?? 0.33;
  const tvaRate = TVA_TAUX[tvaKey]?.rate || 0.10;

  // Un PU forcé sur un poste vaut pour tout le devis (même prix au m² partout,
  // 12/10/2026) : le dernier posé, dans l'ordre des étages, s'applique à chaque
  // pièce qui a ce poste. Résolu à la lecture, pas écrit dans les pièces.
  const prixForce = useMemo(() => {
    const m = new Map();
    for (const r of regrouperParNiveau(rooms).flatMap(g => g.rooms)) {
      for (const l of r.lines || []) if (l.priceOverride != null && l.priceOverride !== '') m.set(l.key, parseFloat(l.priceOverride));
    }
    return m;
  }, [rooms]);

  const coordRate = COORDINATION_TAUX[coordKey]?.rate ?? MARGE_COORDINATION;

  const estimate = useMemo(() => {
    let totalHt = 0;
    // « Ensemble du bien » n'est pas une pièce de 3 × 3 m : ses surfaces sont
    // la SOMME des pièces mesurées (sol, murs, périmètre). Un poste au m² posé
    // là couvre tout le bien ; les 3 × 3 provisoires ne comptent pas.
    const estEnsemble = (room) => (room.customLabel || room.label) === ENSEMBLE;
    const estProvisoire = (room) => !!room.technique || estPieceProvisoire(room);
    const somme = rooms.filter(r => !estEnsemble(r) && !estProvisoire(r)).reduce((acc, r) => {
      const v = r.vertices && r.vertices.length >= 3 ? r.vertices : [];
      const per = polygonPerimeter(v);
      acc.floor += polygonArea(v); acc.perimeter += per; acc.wall += per * (parseFloat(r.height) || WALL_HEIGHT);
      return acc;
    }, { floor: 0, perimeter: 0, wall: 0 });
    const roomEstimates = rooms.map(room => {
      const h = parseFloat(room.height) || WALL_HEIGHT;
      const verts = room.vertices && room.vertices.length >= 3 ? room.vertices : [];
      const ensemble = estEnsemble(room);
      const floorArea = ensemble ? somme.floor : polygonArea(verts);
      const perimeter = ensemble ? somme.perimeter : polygonPerimeter(verts);
      // Sous rampant, une part du mur est perdue (comme au chiffrage serveur).
      const wallArea = ensemble ? somme.wall : perimeter * h * (1 - (Number(room.mansarde) || 0));

      let roomTotal = 0;
      const computedLines = room.lines.map((line, idx) => {
        const item = resolveItem(pricing, line.key);
        if (!item) return null;
        // Un poste a le même prix dans tout le devis : son maxi de grille,
        // retenu d'au plus 5 % selon l'état du bien (étape 1) — en entier pour
        // les supports, à moitié pour les réseaux, à peine pour un équipement
        // (bases.json). Un PU forcé sur ce poste, dans n'importe quelle pièce,
        // vaut partout.
        const unitPrice = prixForce.has(line.key) ? prixForce.get(line.key) : effectivePrice(item, positionSelonEtat(BASES, line.key, item.unit, pos));

        // Smart quantity: for surface items, multiply by room area
        let area = line.qty;
        const unit = item.unit;
        if (unit === 'm²' && line.qty === 1 && floorArea > 0) {
          const isWallItem = WALL_AREA_IDS.has(line.key.split('.')[1]);
          area = isWallItem ? wallArea : floorArea;
        }
        if (unit === 'ml' && line.qty === 1 && floorArea > 0) {
          area = perimeter;
        }

        const lineTotal = area * unitPrice;
        roomTotal += lineTotal;

        return {
          idx,
          key: line.key,
          label: item.label,
          unit,
          qty: area,
          unitPrice,
          total: lineTotal,
          isOverridden: prixForce.has(line.key),
        };
      }).filter(Boolean);

      // Interior partitions (cloisons) from the room plan → derived metré lines
      const CLOISON_DEFS = [
        { kind: 'create', key: 'murs.cloison_placo_std' },
        { kind: 'demolish', key: 'demolition.demolition_cloison_np' },
      ];
      for (const def of CLOISON_DEFS) {
        const len = (room.walls || []).filter(w => w.kind === def.kind).reduce((s, w) => s + polylineLength(w.points), 0);
        const cloisonArea = len * h;
        if (cloisonArea < 0.1) continue;
        const item = resolveItem(pricing, def.key);
        if (!item) continue;
        const unitPrice = effectivePrice(item, positionSelonEtat(BASES, def.key, item.unit, pos)); // état du bien, pondéré
        const lineTotal = cloisonArea * unitPrice;
        roomTotal += lineTotal;
        computedLines.push({
          idx: -1, key: def.key, label: `${item.label} — plan`, unit: item.unit,
          qty: cloisonArea, unitPrice, total: lineTotal, isOverridden: false, auto: true,
        });
      }

      // Add-ons
      const computedAddons = room.addons.map((addon, idx) => {
        let qty = addon.qty;
        if (addon.unit === 'm²' && qty === 1 && floorArea > 0) qty = floorArea;
        if (addon.unit === 'ml' && qty === 1 && floorArea > 0) qty = perimeter;
        const addonTotal = qty * addon.price;
        roomTotal += addonTotal;
        return { ...addon, idx, qty, total: addonTotal };
      });

      totalHt += roomTotal;

      return {
        id: room.id,
        label: room.customLabel || room.label,
        surface: floorArea,
        lines: computedLines,
        addons: computedAddons,
        total: roomTotal,
      };
    });

    const coordination = totalHt * coordRate;
    const htTotal = totalHt + coordination;
    const tva = htTotal * tvaRate;
    const ttc = htTotal + tva;

    return { rooms: roomEstimates, ht: totalHt, coordination, htTotal, tva, ttc, tvaRate, coordRate };
  }, [rooms, pricing, pos, tvaRate, coordRate, prixForce]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Logements & réseaux ───────────────────────────────────────────────────
  // Un logement par niveau habitable d'après les plans, réseaux à refaire si le
  // bien est vétuste ou à rénover ; le métreur corrige (duplex, plateau scindé).
  const proposerLogements = () => {
    marquerRetouche('logements');
    const etat = (state === 'mauvais' || state === 'a_renover') ? 'a_refaire' : 'ok';
    setLogements(logementsDepuisPieces(rooms, { niveauDe, ORDRE_NIVEAUX, polygonArea }, etat));
    setReseauxOuvert(true);
  };
  // Les lignes déduites vont dans une pièce « Installations techniques » par
  // logement (au niveau du logement, pour que le devis se lise logement par
  // logement) et une pièce « Parties communes & bâtiment ». Régénérer ne
  // touche que les lignes « auto » ; ce qu'on y a ajouté à la main reste.
  const genererReseaux = () => {
    marquerRetouche('réseaux');
    const lignes = reseauxDepuisLogements({ logements, regles: BASES.reseaux, modeChauffage })
      .filter(l => resolveItem(pricing, l.key));
    const groupes = [...new Set(lignes.map(l => l.groupe))];
    setRooms(prev => {
      // L'ancienne pièce unique (avant le 12/10 soir) perd ses lignes auto.
      let rs = prev.map(r => ((r.customLabel || r.label) === RESEAUX_PIECE || r.technique) ? { ...r, lines: r.lines.filter(l => l.auto !== 'reseaux') } : r)
        .filter(r => !(((r.customLabel || r.label) === RESEAUX_PIECE || r.technique) && !r.lines.length && !(r.addons || []).length));
      const t = Math.max(Date.now(), ...rs.map(r => Number(r.id) || 0)) + 1;
      groupes.forEach((g, i) => {
        const ls = lignes.filter(l => l.groupe === g).map(l => ({ key: l.key, qty: l.qty, priceOverride: null, auto: 'reseaux', note: l.note }));
        const existante = rs.find(r => r.technique === g);
        if (existante) { rs = rs.map(r => (r !== existante ? r : { ...r, lines: [...r.lines, ...ls] })); return; }
        const lg = logements.find(l => l.nom === g);
        rs.push({
          id: t + i, type: 'autre', label: 'Autre pièce', customLabel: g === COMMUNS ? COMMUNS : PIECE_TECHNIQUE(g), technique: g,
          vertices: rectVertices(3, 3), walls: [], height: String(WALL_HEIGHT), lines: ls, addons: [], photos: [],
          notes: 'Installations techniques déduites des logements — régénérer après un changement des logements.',
          niveau: lg?.niveaux?.[0] || null, valide: true,
        });
      });
      return rs;
    });
  };
  const estTechnique = (r) => !!r.technique || (r.customLabel || r.label) === RESEAUX_PIECE;
  const clesRemplacees = useMemo(() => new Set(Object.values(LIGNES_REMPLACEES).flat()), []);
  const doublonsReseaux = useMemo(() => rooms
    .filter(r => !estTechnique(r))
    .reduce((n, r) => n + (r.lines || []).filter(l => clesRemplacees.has(l.key)).length, 0), [rooms, clesRemplacees]);
  const retirerDoublonsReseaux = () => {
    marquerRetouche('doublons réseaux');
    setRooms(prev => prev.map(r => (estTechnique(r) ? r : { ...r, lines: (r.lines || []).filter(l => !clesRemplacees.has(l.key)) })));
  };
  const resumeReseaux = useMemo(() => {
    const ids = new Set(rooms.filter(estTechnique).map(r => r.id));
    const ers = estimate.rooms.filter(r => ids.has(r.id));
    return ers.length ? { lignes: ers.reduce((n, r) => n + r.lines.length, 0), total: ers.reduce((t, r) => t + r.total, 0) } : null;
  }, [estimate, rooms]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Send devis to client by email ─────────────────────────────────────────
  const sendToClient = async () => {
    if (!client.email) { setSendError('Email client manquant'); return; }
    setSendingClient(true);
    setSendError('');
    try {
      const payload = {
        client,
        projectPhotos: projectPhotos.map(p => p.url).filter(Boolean),
        rooms: estimate.rooms.map(r => {
          const srcRoom = rooms.find(rm => rm.id === r.id);
          return {
            label: r.label,
            surface: r.surface.toFixed(1),
            lines: r.lines.map(l => ({ label: l.label, qty: l.qty.toFixed(1), unit: l.unit, total: fmt(l.total) })),
            addons: r.addons.map(a => ({ label: a.label, qty: a.qty.toFixed(1), unit: a.unit, total: fmt(a.total) })),
            photos: (srcRoom?.photos || []).map(p => ({ url: p.url, caption: p.caption || '' })).filter(p => p.url),
            total: fmt(r.total),
          };
        }),
        totals: {
          ht: fmt(estimate.htTotal),
          coordination: fmt(estimate.coordination),
          coordRate,
          tva: fmt(estimate.tva),
          tvaLabel: `${(tvaRate * 100).toFixed(1)}%`,
          ttc: fmt(estimate.ttc),
        },
      };
      const res = await proFetch('/send-to-client', {
        method: 'POST',

        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur envoi email');
      setSentClient(true);
      // Auto-advance pipeline to "devis_envoye"
      if (ref) {
        proFetch(`/leads/${ref}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'devis_envoye' }) }).catch(() => {});
      }
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSendingClient(false);
    }
  };

  // Un rendu tombe sur sa pièce. Quand la photo « avant » était un document
  // scanné, le serveur renvoie `avant` = la photographie découpée dedans :
  // elle rejoint les photos de la pièce (c'est elle que l'avant/après montre).
  const poserRendu = (roomId, rendu) => {
    const { avant, ...afterPhoto } = rendu || {};
    const patch = { afterPhoto };
    const photos = rooms.find(r => r.id === roomId)?.photos || [];
    if (avant?.id && !photos.some(p => p.id === avant.id)) {
      patch.photos = [...photos, { id: avant.id, url: avant.url, caption: 'Photo découpée dans le document scanné', recadreeDe: avant.recadreeDe || null }];
    }
    updateRoom(roomId, patch);
  };

  // ── Open dossier de présentation (avant/après + devis pièce par pièce) ───
  const openDossier = () => {
    // Valeurs brutes (numériques) — le dossier fait son propre formatage et le
    // regroupement par lot (line.key.split('.')[0]).
    const dossierData = {
      client,
      titre,
      state: stateMultiplier[state]?.label || state,
      tvaLabel: `${(tvaRate * 100).toFixed(1)}%`,
      tvaRate,
      date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
      catLabels: CAT_LABELS,
      logements,
      style,
      rooms: estimate.rooms.map((roomEst, i) => {
        const room = rooms[i];
        return {
          label: roomEst.label,
          niveau: room.niveau || niveauDuNom(roomEst.label) || null,
          surface: roomEst.surface,
          // Un lieu vu sur les photos mais jamais coté (3 × 3 m provisoires) et
          // « Ensemble du bien » ne sont pas des pièces de 9 m² : le dossier
          // les présente autrement et ne les compte pas dans la surface.
          provisoire: estPieceProvisoire(room) && !room.provisoireAssume,
          technique: room.technique || null,
          type: room.type || null,
          ensemble: (room.customLabel || room.label) === ENSEMBLE,
          vertices: room.vertices,
          walls: room.walls || [],
          height: room.height,
          notes: room.notes || '',
          photos: (room.photos || []).map(p => ({ id: p.id, url: p.url, src: p.src, caption: p.caption || '', timestamp: p.timestamp || '' })),
          afterPhoto: room.afterPhoto ? { id: room.afterPhoto.id, url: room.afterPhoto.url, fromPhotoId: room.afterPhoto.fromPhotoId } : null,
          lines: roomEst.lines.map(l => ({
            key: l.key,
            label: l.label,
            unit: l.unit,
            qty: l.qty,
            unitPrice: l.unitPrice,
            total: l.total,
          })),
          addons: (roomEst.addons || []).map(a => ({
            label: a.label,
            unit: a.unit,
            qty: a.qty,
            price: a.price || 0,
            total: a.total,
          })),
          total: roomEst.total,
        };
      }),
      projectPhotos: projectPhotos.map(p => ({ id: p.id, url: p.url, src: p.src, caption: p.caption || '' })),
      estimate: {
        ht: estimate.ht,
        coordination: estimate.coordination,
        coordRate,
        htTotal: estimate.htTotal,
        tva: estimate.tva,
        ttc: estimate.ttc,
      },
    };
    // localStorage (pas sessionStorage) : partagé entre onglets de façon fiable
    // — Safari iPad ne copie pas toujours sessionStorage vers un window.open
    try {
      localStorage.setItem('pro_dossier', JSON.stringify(dossierData));
    } catch {
      // quota plein (grosses photos base64) : retente sans les data-URL
      const slim = { ...dossierData, rooms: dossierData.rooms.map(r => ({ ...r, photos: r.photos.map(p => p.url ? { ...p, src: undefined } : p) })) };
      try { localStorage.setItem('pro_dossier', JSON.stringify(slim)); } catch { sessionStorage.setItem('pro_dossier', JSON.stringify(slim)); }
    }
    window.open('/pro/dossier', '_blank');
  };

  // ── Suggestions for active room ─────────────────────────────────────────
  const suggestions = useMemo(() => {
    if (!activeRoomId) return [];
    const room = rooms.find(r => r.id === activeRoomId);
    if (!room) return [];

    const existingKeys = new Set(room.lines.map(l => l.key));
    const seen = new Set();
    const result = [];

    for (const line of room.lines) {
      for (const rule of SUGGESTION_RULES) {
        if (line.key !== rule.trigger) continue;

        for (const suggestKey of rule.suggest) {
          if (existingKeys.has(suggestKey)) continue;
          if (seen.has(suggestKey)) continue;
          const item = resolveItem(pricing, suggestKey);
          if (!item) continue;
          seen.add(suggestKey);
          result.push({ key: suggestKey, label: item.label, reason: rule.reason, trigger: line.key });
        }
      }
    }
    return result;
  }, [activeRoomId, rooms, pricing]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Active room + step navigation ──────────────────────────────────────
  const activeRoom = rooms.find(r => r.id === activeRoomId);

  // Les croquis cotés photographiés pendant la visite : l'inspection les a
  // reconnus (observation « plan ») et lus ; ce sont les plans de référence
  // de l'écran Plans, rangés au niveau écrit dessus.
  const plansDossier = useMemo(() => {
    const d = lead?.dossier;
    if (!d) return [];
    return (d.observationsPhotos || []).filter(o => o.plan).map(o => {
      const photo = (d.photos || []).find(p => p.nom === o.nom);
      return photo ? { id: photo.id, url: `/api/photo/${photo.id}`, niveau: o.plan.niveau || '', nom: o.nom } : null;
    }).filter(Boolean);
  }, [lead]);

  // ── Wizard global : 1=Client, 2=Ressources, 3=Constats, 4=Plans, 5=Postes, 6=Style, 7=Récap ──
  const [step, setStepBrut] = useState(1);
  // L'étape 4 (Plans) n'existe plus : tout lien ou mémoire qui y mène ouvre Pièces.
  const setStep = useCallback((v) => setStepBrut(prev => { const n = typeof v === 'function' ? v(prev) : v; return n === 4 ? 5 : n; }), []);
  const [vueNiveaux, setVueNiveaux] = useState(false);

  // Reprendre le wizard à l'étape où on était après un reload (par lead)
  React.useEffect(() => {
    if (!ref || !hydrated) return;
    const saved = parseInt(sessionStorage.getItem(`pro_step:${ref}`), 10);
    if (saved >= 2 && saved <= 7 && (saved <= 3 || rooms.length > 0)) setStep(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, hydrated]);
  React.useEffect(() => {
    // hydrated obligatoire : sinon l'écriture au montage (step=1) écrase
    // l'étape sauvée avant que la restauration ne la lise
    if (ref && hydrated) sessionStorage.setItem(`pro_step:${ref}`, String(step));
  }, [ref, hydrated, step]);
  // Le configurateur en ligne note le détail d'un poste sous trois formes :
// « 12 u » (quantité voulue), « 8 ml → 20.0 m² » (quantité voulue, en ml) et
// « ~42 m² » (déduit de la surface déclarée). Seules les deux premières portent
// un choix du client ; la troisième sera recalculée sur le relevé. Tout
// ramener à 1 faisait passer douze prises pour une seule.
function quantiteDemandee(detail) {
  const d = String(detail || '').trim();
  if (!d || d.startsWith('~')) return 1;
  const m = d.match(/^(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) || 1 : 1;
}

const EMOJI_PAR_NOM = [[/ensemble du bien/i, '🧰'], [/cave|sous-sol|chaufferie|local technique|local enterr/i, '🧱'], [/garage|atelier/i, '🚗'], [/balcon|terrasse|jardin|ext[ée]rieur/i, '🌤️'],
  [/escalier|palier|cage/i, '🪜'], [/comble|grenier/i, '🔺'], [/buanderie|cellier|arri[èe]re.cuisine|d[ée]barras|dressing|placard/i, '🧺'],
  [/bureau/i, '🖥️'], [/\bwc\b|toilette/i, '🚽'], [/salle de bain|sdb|salle d'eau|douche/i, '🚿'], [/cuisine/i, '🍳'],
  [/chambre/i, '🛏️'], [/s[ée]jour|salon|salle [àa] manger|pi[èe]ce de vie/i, '🛋️'], [/couloir|entr[ée]e|d[ée]gagement/i, '🚪'], [/fa[çc]ade|toiture/i, '🏠']];
const EMOJI_PAR_TYPE = { sejour: '🛋️', cuisine: '🍳', chambre: '🛏️', sdb: '🚿', wc: '🚽', couloir: '🚪', facade: '🏠', autre: '📐' };
const emojiPiece = (room) => (EMOJI_PAR_NOM.find(([re]) => re.test(room.customLabel || ''))?.[1]) || EMOJI_PAR_TYPE[room.type] || '📐';

// Pour comparer deux relevés : un nombre écrit en texte vaut le nombre, une
// valeur absente vaut null.
const normaliser = (k, v) => (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : (v === undefined ? null : v));

// Un poste « chaîne complète » contient sa préparation et sa finition : à
// côté d'un enduit ou d'un décollage sur la même pièce, on compte deux
// fois. L'analyse ne le fait plus ; la page le voit encore sur un relevé
// ancien ou saisi à la main, et propose de ramener la chaîne à sa finition.
const RECOUVREMENTS = {
  'murs.peinture_chaine_complete': { finition: 'murs.peinture_murs', couvre: ['preparation.decollage_papier', 'preparation.enduit_lissage', 'preparation.ratissage', 'preparation.application_primaire', 'preparation.preparation_support', 'preparation.grattage_peinture', 'murs.enduit_rebouchage', 'murs.peinture_murs'] },
  'murs.plafond_chaine_complete': { finition: 'murs.peinture_plafond', couvre: ['murs.peinture_plafond', 'murs.enduit_rebouchage', 'preparation.poncage_murs'] },
};

const LIBELLES_CLIENT = { prenom: 'Prénom', nom: 'Nom ou raison sociale', telephone: 'Téléphone', email: 'E-mail' };

// Repères de marché du prix au m² d'une rénovation (€ HT/m², ordres de
// grandeur couramment publiés) : de quoi situer le devis en un mot devant
// le client — pas une grille, le référentiel Apex reste la seule source.
const REPERES_M2 = [
  { max: 500, label: 'rafraîchissement', plage: '250 – 500 €' },
  { max: 1000, label: 'rénovation moyenne', plage: '500 – 1 000 €' },
  { max: 2000, label: 'rénovation lourde', plage: '1 000 – 2 000 €' },
  { max: Infinity, label: 'rénovation très lourde / restructuration', plage: '> 2 000 €' },
];
const repereDe = (prixM2) => REPERES_M2.find(r => prixM2 < r.max);

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`rounded-xl px-4 py-3 ${accent ? 'bg-slate-900 text-white' : 'bg-slate-50'}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-wide ${accent ? 'text-slate-300' : 'text-slate-400'}`}>{label}</div>
      <div className={`text-xl font-black tabular-nums mt-0.5 ${accent ? 'text-orange-400' : 'text-slate-900'}`}>{value}</div>
      {sub && <div className={`text-[11px] mt-0.5 ${accent ? 'text-slate-300' : 'text-slate-500'}`}>{sub}</div>}
    </div>
  );
}

// Une répartition en barres : chaque part en € et en % du total.
function Barres({ lignes, total }) {
  return (
    <div className="space-y-1.5">
      {lignes.map(({ label, value }) => {
        const pct = total ? (value / total) * 100 : 0;
        return (
          <div key={label} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-2 text-xs">
            <span className="truncate text-slate-700" title={label}>{label}</span>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.max(1, pct)}%` }} /></div>
            <span className="tabular-nums text-slate-600 whitespace-nowrap">{fmt(value)} € <span className="text-slate-400">· {pct.toFixed(0)} %</span></span>
          </div>
        );
      })}
    </div>
  );
}

// Le devisage en écrans successifs, dans l'ordre où l'on construit un devis :
// qui, ce qu'on a rapporté, ce qu'on en retient, le bien étage par étage,
// les postes pièce par pièce, le style et ses rendus, le récapitulatif.
const WIZARD_STEPS = [
    { n: 1, label: 'Client', icon: '👤' },
    { n: 2, label: 'Ressources', icon: '🎙️' },
    { n: 3, label: 'Constats', icon: '🔎' },
    // L'ancienne étape Plans (4) est fondue dans Pièces : la vue des niveaux est
    // en tête de l'écran, le plan d'une pièce s'ouvre depuis la pièce.
    { n: 5, label: 'Pièces', icon: '🔨' },
    { n: 6, label: 'Style', icon: '🎨' },
    { n: 7, label: 'Récap', icon: '📄' },
  ];
  const DERNIERE = 7;
  // Quand on arrive aux étapes par pièce sans pièce active, sélectionne la première
  React.useEffect(() => {
    if (step >= 4 && step <= 6 && !activeRoomId && rooms.length) {
      setActiveRoomId(rooms[0].id);
    }
  }, [step, activeRoomId, rooms]);
  // À l'arrivée sur Postes, on reprend à la première pièce non validée, dans
  // l'ordre des étages. Échap ferme la pièce ouverte en grand.
  const etapePrecedente = React.useRef(step);
  React.useEffect(() => {
    if (step === 5 && etapePrecedente.current !== 5 && rooms.length) {
      const ordre = regrouperParNiveau(rooms).flatMap(g => g.rooms);
      const suivante = ordre.find(r => !r.valide) || ordre[0];
      if (suivante) setActiveRoomId(suivante.id);
    }
    if (step !== 4 && step !== 5) setPlanOuvert(false);
    etapePrecedente.current = step;
  }, [step, rooms]);
  // Changer d'écran ramène en haut : le nouvel écran commence à son titre,
  // pas au milieu du précédent.
  React.useEffect(() => {
    if (etapePrecedente.current !== step) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);
  React.useEffect(() => {
    if (!planOuvert) return undefined;
    const k = (e) => { if (e.key === 'Escape') setPlanOuvert(false); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [planOuvert]);

  // ── Auto-advance pipeline: estimatif → visite when ≥1 room with ≥1 line ──
  const hasContent = rooms.some(r => r.lines.length > 0);
  const [advancedToVisite, setAdvancedToVisite] = useState(false);
  React.useEffect(() => {
    if (ref && hasContent && !advancedToVisite && lead?.status !== 'visite' && lead?.status !== 'devis_envoye' && lead?.status !== 'accepte' && lead?.status !== 'en_travaux') {
      proFetch(`/leads/${ref}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'visite' }) }).catch(() => {});
      setAdvancedToVisite(true);
    }
  }, [ref, hasContent, advancedToVisite, lead]);

  // ── Search for pricing items ──────────────────────────────────────────────
  const [itemSearch, setItemSearch] = useState('');
  const filteredItems = useMemo(() => {
    if (!itemSearch.trim()) return allItems;
    const q = itemSearch.toLowerCase();
    return allItems.filter(i => i.label.toLowerCase().includes(q) || i.key.toLowerCase().includes(q));
  }, [allItems, itemSearch]);

  // Category labels for grouping
  const CAT_LABELS = {
    sols: 'Sols', murs: 'Murs/Plafonds', preparation: 'Préparation supports', plomberie: 'Plomberie',
    electricite: 'Électricité', menuiseries: 'Menuiseries', isolation: 'Isolation', chauffage: 'Chauffage',
    demolition: 'Démolition', divers: 'Divers', ite: 'ITE', facade: 'Façade',
  };

  // Ce qui explique le devis au client : la surface réellement cotée, le prix
  // au m² qui en découle, d'où vient l'argent (corps d'état, étages, postes
  // lourds) et la part des supports, des réseaux et des équipements.
  const lecture = useMemo(() => {
    const provisoire = (r) => !!r.technique || estPieceProvisoire(r) || (r.customLabel || r.label) === ENSEMBLE;
    const surfaceDe = (id) => estimate.rooms.find(e => e.id === id)?.surface || 0;
    const mesurees = rooms.filter(r => !provisoire(r) && (r.vertices?.length || 0) >= 3);
    const surface = mesurees.reduce((s, r) => s + surfaceDe(r.id), 0);
    const parLot = {}, parNiveau = {}, lignes = [];
    const parts = { supports: 0, reseaux: 0, equipements: 0 };
    for (const er of estimate.rooms) {
      const room = rooms.find(r => r.id === er.id);
      const niv = room ? (niveauDe(room) || 'Niveau non précisé') : '—';
      parNiveau[niv] = (parNiveau[niv] || 0) + er.total;
      for (const l of er.lines) {
        const cat = String(l.key).split('.')[0];
        parLot[cat] = (parLot[cat] || 0) + l.total;
        lignes.push({ ...l, piece: er.label });
        const sens = sensibiliteEtat(BASES, l.key, l.unit);
        parts[sens >= 1 ? 'supports' : sens >= 0.5 ? 'reseaux' : 'equipements'] += l.total;
      }
      for (const a of er.addons || []) { parLot._extras = (parLot._extras || 0) + a.total; parts.equipements += a.total; }
    }
    const tri = (o) => Object.entries(o).map(([k, v]) => ({ label: k, value: v })).sort((a, b) => b.value - a.value);
    return {
      surface, nbMesurees: mesurees.length, nbProvisoires: rooms.length - mesurees.length,
      prixM2: surface > 0 ? estimate.htTotal / surface : null,
      prixM2TTC: surface > 0 ? estimate.ttc / surface : null,
      parLot: tri(parLot), parNiveau: tri(parNiveau),
      top: lignes.sort((a, b) => b.total - a.total).slice(0, 5),
      parts, nbLignes: lignes.length,
      rendus: rooms.filter(r => r.afterPhoto).length,
    };
  }, [rooms, estimate]);

  if (loading) return <div className="text-center py-20 text-slate-400">Chargement du lead...</div>;

  // Les pièces dans l'ordre où l'on parcourt le bien : étage par étage.
  const ordrePieces = regrouperParNiveau(rooms).flatMap(g => g.rooms);
  const indexPiece = ordrePieces.findIndex(r => r.id === activeRoomId);
  const allerPiece = (d) => { const cible = ordrePieces[indexPiece + d]; if (cible) setActiveRoomId(cible.id); };
  // Valider une pièce à l'étape Postes : on la marque, la suivante s'ouvre ;
  // après la dernière, le style.
  // Valider une pièce dont les dimensions sont provisoires demande de les
  // saisir, ou d'assumer explicitement l'estimation : plus de 3 × 3 silencieux.
  const validerPiece = (assumer = false) => {
    if (!activeRoom) return;
    if (estPieceProvisoire(activeRoom) && !activeRoom.provisoireAssume && !assumer) { setAAssumer(activeRoom.id); return; }
    setAAssumer(null);
    updateRoom(activeRoom.id, { valide: true, ...(assumer ? { provisoireAssume: true } : {}) });
    setValideesIci(prev => new Set(prev).add(activeRoom.id));
    if (indexPiece < ordrePieces.length - 1) setActiveRoomId(ordrePieces[indexPiece + 1].id);
    else setStep(6);
  };
  // Les photos de plan qui montrent la pièce active : celle dont elle vient, sinon celles de son niveau.
  const plansDeLaPiece = activeRoom
    ? (plansDossier.filter(p => p.id === activeRoom.planPhotoId).length
        ? plansDossier.filter(p => p.id === activeRoom.planPhotoId)
        : plansDossier.filter(p => (niveauDuNom(p.niveau || '') || p.niveau) === niveauDe(activeRoom)))
    : [];

  // En-tête (nom, niveau, dupliquer, supprimer) et plan de la pièce active :
  // dans la modale de l'étape Plans, en tête des étapes Postes et Style.
  const enTetePiece = activeRoom ? (
<div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <span className="text-2xl leading-none shrink-0">{emojiPiece(activeRoom)}</span>
      <input
        type="text"
        value={activeRoom.customLabel || activeRoom.label}
        onChange={e => updateRoom(activeRoom.id, { customLabel: e.target.value })}
        title={activeRoom.customLabel || activeRoom.label}
        className="w-full min-w-0 text-xl font-black text-slate-900 bg-transparent border-none outline-none focus:bg-slate-50 px-2 py-1 rounded-lg -ml-2"
      />
    </div>
    <div className="flex gap-2 shrink-0 items-center">
      <select value={ORDRE_NIVEAUX.includes(niveauDe(activeRoom)) ? niveauDe(activeRoom) : ''} onChange={e => updateRoom(activeRoom.id, { niveau: e.target.value || null })} title="Niveau"
              className="select-compact px-2 py-1.5 border border-slate-200 rounded-lg text-xs bg-white outline-none">
        <option value="">Niveau…</option>
        {ORDRE_NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <button onClick={() => duplicateRoom(activeRoom.id)} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-semibold text-slate-600">Dupliquer</button>
      <button onClick={() => requestRemoveRoom(activeRoom.id)}
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
          confirmDeleteId === activeRoom.id ? 'bg-red-600 text-white' : 'bg-red-50 hover:bg-red-100 text-red-600'
        }`}>
        {confirmDeleteId === activeRoom.id ? 'Confirmer la suppression ?' : 'Supprimer'}
      </button>
    </div>
  </div>
</div>
  ) : null;
  const planPiece = activeRoom ? (
<div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
  <RoomCanvas
    key={activeRoom.id}
    vertices={activeRoom.vertices}
    height={activeRoom.height}
    walls={activeRoom.walls}
    onChange={({ vertices, height, walls }) => updateRoom(activeRoom.id, { vertices, height, walls })}
  />

  {/* Photos de la pièce — disponibles dès l'étape plan */}
  <div className="mt-5 pt-5 border-t border-slate-100">
    <div className="flex items-center gap-2 mb-3">
      <span className="text-sm font-bold text-slate-900">📷 Photos de la pièce</span>
    </div>
    <PhotoCapture
      photos={activeRoom.photos}
      onUpload={uploadPhotoToS3}
      onAdd={(photo, replaceIdx) => {
        const photos = [...activeRoom.photos];
        if (replaceIdx !== undefined) photos[replaceIdx] = photo;
        else photos.push(photo);
        updateRoom(activeRoom.id, { photos });
      }}
      onRemove={(idx) => updateRoom(activeRoom.id, { photos: activeRoom.photos.filter((_, i) => i !== idx) })}
    />
  </div>

  {/* Notes terrain */}
  <div className="mt-5 pt-5 border-t border-slate-100">
    <label className="block text-sm font-bold text-slate-900 mb-2">📝 Notes terrain</label>
    <textarea
      value={activeRoom.notes}
      onChange={e => updateRoom(activeRoom.id, { notes: e.target.value })}
      placeholder="Observations sur site : état des supports, contraintes d'accès, points d'attention…"
      rows={4}
      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:border-orange-500 outline-none resize-none"
    />
  </div>
</div>
  ) : null;

  return (
    <Page className="pb-32">
      <EnTetePage titre={ref ? `Devis ${ref}` : 'Nouveau devis'}
                  sous={[client.prenom, client.nom].filter(Boolean).join(' ') || 'Relevé, chiffrage et envoi au client.'} />

      {dossierPlusRecent && lead?.dossier && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 mb-3 text-sm text-orange-900">
          <span>
            <span className="font-semibold">Le dossier de visite a été ré-analysé</span>
            {lead.dossier.analyseLe ? ` le ${new Date(lead.dossier.analyseLe).toLocaleDateString('fr-FR')} à ${new Date(lead.dossier.analyseLe).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
            , après ce relevé. Le reprendre remplace les pièces et les lignes actuelles.
          </span>
          <span className="flex gap-2 ml-auto">
            <button onClick={() => reprendreDepuisDossier(lead)}
                    className="px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-bold hover:bg-orange-700">Reprendre le relevé depuis le dossier</button>
            <button onClick={() => setDossierPlusRecent(false)}
                    className="px-3 py-1.5 rounded-lg border border-orange-300 text-xs font-semibold hover:bg-orange-100">Garder ce relevé</button>
          </span>
        </div>
      )}

      {step === 5 && repriseDossier && (
        <div className={`flex flex-wrap items-baseline gap-x-2 rounded-xl border px-4 py-2.5 mb-3 text-sm ${repriseDossier.sansCotes ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
          <span className="font-semibold">Relevé repris du dossier de visite</span>
          <span>
            {repriseDossier.date ? `du ${new Date(repriseDossier.date).toLocaleDateString('fr-FR')} ` : ''}
            {repriseDossier.sansCotes
              ? '— aucune cote n’avait été saisie : les pièces viennent des lieux reconnus sur les photos, en 3 × 3 m provisoires. Donnez-leur leurs vraies dimensions ; lignes et photos sont déjà en place.'
              : '— pièces mesurées, lignes proposées et photos sont en place ; ce qui ne se rattache à aucune pièce est dans « Ensemble du bien » ou dans une pièce de lieu (cave, façade…). Corrigez ce qui doit l’être ; rien n’est à ressaisir.'}
          </span>
        </div>
      )}

      {/* ── Barre de progression du wizard ── */}
      <div className="bg-white rounded-xl p-2 shadow-sm border border-slate-100 mb-3">
        <div className="flex items-center gap-1">
          {WIZARD_STEPS.map(({ n, label, icon }, i) => {
            const reachable = n <= 3 || rooms.length > 0; // à partir des plans, il faut au moins une pièce
            return (
              <React.Fragment key={n}>
                {i > 0 && <div className={`h-0.5 flex-1 ${step > WIZARD_STEPS[i - 1].n ? 'bg-orange-500' : 'bg-slate-200'}`} />}
                <button
                  onClick={() => { if (reachable) setStep(n); }}
                  disabled={!reachable}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors min-h-[40px] ${
                    step === n ? 'bg-orange-600 text-white shadow-sm'
                    : step > n ? 'text-orange-600'
                    : reachable ? 'text-slate-500 hover:bg-slate-50' : 'text-slate-300 cursor-not-allowed'
                  }`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${step === n ? 'bg-white text-orange-600' : step > n ? 'bg-orange-100' : 'bg-slate-100'}`}>
                    {step > n ? '✓' : n}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Bannière mode dégradé : relevé restauré depuis le brouillon local */}
      {restoredFromDraft && saveState !== 'saved' && (
        <div className="bg-amber-50 border-2 border-amber-300 text-amber-800 rounded-xl px-4 py-3 text-sm font-bold mb-3">
          📴 Relevé restauré depuis la sauvegarde locale de cette tablette — synchronisation automatique dès que le réseau revient.
        </div>
      )}

      {/* ════════ ÉTAPE 1 — CLIENT ════════ */}
      {step === 1 && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <h2 className="text-lg font-black text-slate-900 mb-1">Le client</h2>
          <p className="text-sm text-slate-400 mb-5">Coordonnées et adresse du chantier.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['prenom', 'nom', 'telephone', 'email'].map(field => (
              <div key={field}>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{LIBELLES_CLIENT[field] || field}</label>
                <input type={field === 'email' ? 'email' : field === 'telephone' ? 'tel' : 'text'} value={client[field]}
                  onChange={e => setClient(prev => ({ ...prev, [field]: e.target.value }))}
                  className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none min-h-[44px]" />
              </div>
            ))}
            <details className="sm:col-span-2 rounded-lg border-2 border-slate-100 px-3 py-2" open={!!(client.siege || client.siren || client.tvaIntra)}>
              <summary className="text-xs font-semibold text-slate-500 cursor-pointer min-h-[32px] flex items-center">Client professionnel (société) — le nom porte la raison sociale</summary>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                {[['siege', 'Siège social'], ['siren', 'SIREN'], ['tvaIntra', 'TVA intracommunautaire']].map(([field, libelle]) => (
                  <div key={field}>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">{libelle}</label>
                    <input type="text" value={client[field] || ''} onChange={e => setClient(prev => ({ ...prev, [field]: e.target.value }))}
                      className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none min-h-[44px]" />
                  </div>
                ))}
              </div>
            </details>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Titre du projet <span className="font-normal text-slate-400">(couverture du dossier — vide : déduit du type de bien)</span></label>
              <input type="text" value={titre} onChange={e => setTitre(e.target.value)} placeholder="Ex. Extension du centre de santé dentaire"
                className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none min-h-[44px]" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Adresse du chantier</label>
              <AddressAutocomplete value={client.adresse} onChange={(data) => setClient(prev => ({ ...prev, ...data }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">État du bien</label>
              <select value={state} onChange={e => setState(e.target.value)} className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg text-sm bg-white outline-none focus:border-orange-500 min-h-[44px]">
                {Object.entries(stateMultiplier).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">TVA</label>
              <select value={tvaKey} onChange={e => setTvaKey(e.target.value)} className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg text-sm bg-white outline-none focus:border-orange-500 min-h-[44px]">
                {Object.entries(TVA_TAUX).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Coordination de chantier</label>
              <select value={coordKey} onChange={e => setCoordKey(e.target.value)} className="w-full px-3 py-3 border-2 border-slate-200 rounded-lg text-sm bg-white outline-none focus:border-orange-500 min-h-[44px]">
                {Object.entries(COORDINATION_TAUX).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ════════ ÉTAPE 2 — RESSOURCES : dictées, vidéos, photos, documents ════════ */}
      {step === 2 && (
        <div className="space-y-3">
          {ref && lead?.dossier && <DossierVisite reference={ref} section="ressources" />}
          {ref && !lead?.dossier && (
            // Un devis venu d'une demande en ligne n'a pas encore de relevé :
            // le même dépôt que « Nouveau devis », rattaché à ce lead.
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <h2 className="text-lg font-black text-slate-900 mb-1">Le relevé de visite</h2>
              <p className="text-sm text-slate-400 mb-4">Dictées, photos, plans et cotes de la visite : l'analyse construit le dossier, les pièces et les postes de ce devis.</p>
              <ReleveDeposeur refExistant={ref} libelle="Analyser le relevé"
                              chantier={{ ...client, typeBien: lead?.dossier?.chantier?.typeBien || 'maison', etat: state, tva: TVA_TAUX[tvaKey]?.rate ?? 0.1 }}
                              onTermine={() => { try { sessionStorage.setItem(`pro_reanalyse:${ref}`, '1'); } catch {} window.location.reload(); }} />
            </div>
          )}
        </div>
      )}

      {/* ════════ ÉTAPE 3 — CONSTATS : ce qu'on retient de la visite ════════ */}
      {step === 3 && (
        <div className="space-y-3">
          {ref && <DossierVisite reference={ref} section="constats" onReanalyse={() => { try { sessionStorage.setItem(`pro_reanalyse:${ref}`, '1'); } catch {} window.location.reload(); }} />}
          {lead?.dossier?.releve?.etatPourquoi && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-sm">
              <span className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">État du bien retenu</span>
              <span className="font-semibold text-slate-800">{stateMultiplier[state]?.label || state}</span>
              <span className="text-slate-500"> — {lead.dossier.releve.etatPourquoi}</span>
            </div>
          )}
      {aUneDemande(lead) && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-3 overflow-hidden">
          <button onClick={() => setDemandeOuverte(o => !o)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors">
            <span className="text-sm font-semibold text-slate-700">
              {titreDemande(lead)}
              <span className="font-normal text-slate-400"> — {resumeDemande(lead)}{lead.context.total ? `, ${lead.context.total}` : ''}</span>
            </span>
            <svg className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${demandeOuverte ? 'rotate-180' : ''}`}
                 viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {demandeOuverte && (
            <div className="px-4 pb-4 border-t border-slate-100 pt-4">
              <EstimatifClient lead={lead} />
            </div>
          )}
        </div>
      )}
          {!lead?.dossier && !aUneDemande(lead) && (
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 text-sm text-slate-500">Aucun constat pour l'instant : ni dossier de visite analysé, ni demande en ligne.</div>
          )}
        </div>
      )}

      {/* ════════ ÉTAPE 4 — PLANS : le bien étage par étage ════════ */}
      {step === 5 && vueNiveaux && (
        <div className="mb-4">
          <PlanNiveaux rooms={rooms} estimate={estimate} plans={plansDossier} emoji={emojiPiece} roomTypes={ROOM_TYPES}
                       activeRoomId={activeRoomId} onSelect={(id) => { setActiveRoomId(id); setVueNiveaux(false); }}
                       onAddRoom={(typeId, niveau) => { addRoom(typeId, niveau); setPlanOuvert(true); }} />
          {rooms.length > 0 && <p className="text-xs text-slate-400 mt-2">Touchez une pièce pour la vérifier ; « Plan &amp; cotes » ouvre son contour à côté du plan photographié.</p>}
        </div>
      )}

      {/* ── La pièce en grand : photo du plan à gauche, contour, hauteur, photos et notes à droite ── */}
      {(step === 4 || step === 5) && planOuvert && activeRoom && (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-2 sm:p-6" onClick={() => setPlanOuvert(false)}>
          <div className="bg-slate-50 rounded-2xl shadow-2xl w-full max-w-[1400px] max-h-[94vh] overflow-y-auto p-4 sm:p-5" onClick={e => e.stopPropagation()}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {niveauDe(activeRoom) || 'Niveau non précisé'} · pièce {indexPiece + 1} / {ordrePieces.length}
              </p>
              <div className="flex gap-2">
                <button onClick={() => allerPiece(-1)} disabled={indexPiece <= 0}
                        className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 disabled:opacity-40">← Précédente</button>
                <button onClick={() => allerPiece(1)} disabled={indexPiece >= ordrePieces.length - 1}
                        className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 disabled:opacity-40">Suivante →</button>
                <button onClick={() => setPlanOuvert(false)} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-bold">Fermer ✕</button>
              </div>
            </div>
            {enTetePiece}
            <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-4 mt-4 items-start">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 lg:sticky lg:top-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                  {activeRoom.planPhotoId && plansDeLaPiece.some(pl => pl.id === activeRoom.planPhotoId) ? 'Plan d\'où viennent les cotes de cette pièce' : `Plan de référence${plansDeLaPiece.length > 1 ? 's' : ''} du niveau`}
                </p>
                {plansDeLaPiece.length ? plansDeLaPiece.map(pl => (
                  <a key={pl.id} href={pl.url} target="_blank" rel="noreferrer" title="Ouvrir en pleine taille" className="block mb-2 last:mb-0">
                    <img src={pl.url} alt={pl.nom || 'plan'} className="w-full rounded-lg border border-slate-200" />
                  </a>
                )) : (
                  <div className="h-40 rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400 text-center px-4">
                    Aucun plan photographié pour ce niveau — le contour se trace à droite d'après les cotes.
                  </div>
                )}
              </div>
              {planPiece}
            </div>
          </div>
        </div>
      )}

      {/* ── Étape 6 : style du projet — proposé d'après la dictée, choisi, puis rendu ── */}
      {step === 6 && rooms.length > 0 && (
        <div className="mb-4 space-y-3">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <h2 className="text-lg font-black text-slate-900">🎨 Style de la rénovation</h2>
            <p className="text-sm text-slate-400">Palette appliquée à tous les rendus avant/après — seules les surfaces réellement travaillées sont modifiées.</p>
          </div>
          {ref && <StyleSuggestion leadRef={ref} disponible={!!lead?.dossier?.transcription} auto={!style?.ambiance}
                                   onApply={({ style: st, brief }) => { setStyle(prev => ({ ...prev, ...st })); if (brief) setClientBrief(brief); }} />}
          <StyleStudio style={style} onChange={setStyle} rooms={rooms} />
        </div>
      )}
      {/* Les rendus restent montés à toutes les étapes : chaque pièce validée part
          au rendu dès que le style est choisi, et le suivi ne s'arrête pas quand
          on change d'écran. Visibles à l'étape Style. */}
      {ref && rooms.length > 0 && (
        <div className={step === 6 ? 'mb-4' : 'hidden'}>
          <PhotosPresentation leadRef={ref} rooms={rooms} style={style} clientBrief={clientBrief} emoji={emojiPiece}
                              onResult={poserRendu} niveauDe={niveauDe} auto={!!style?.ambiance} autoIds={valideesIci} />
        </div>
      )}

      {/* Les photos du chantier sont celles du dossier de visite (étape 2, en haut) : le bloc « Photos chantier » les répétait. Les photos du projet restent en données (couverture du dossier). */}



        {/* ──── ROOM EDITOR (étapes 5 Postes / 6 Style) — à l'étape Plans, la pièce s'ouvre en modale ──── */}
        {step >= 5 && step <= 6 && (
        <div className="space-y-4">
          {!activeRoom ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-slate-100 text-center text-slate-400">
              <p className="text-lg font-semibold">Aucune pièce pour l'instant</p>
              <p className="text-sm mt-2">Ajoutez-en avec « + Pièce », ou relevez le bien avec le Deviseur.</p>
            </div>
          ) : (
            <>
              {step !== 6 && enTetePiece}

              {/* ═══ ÉTAPE 5 : Travaux — une pièce après l'autre ═══ */}
              {step === 5 && (
                <>
                  <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-orange-100">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          Pièce {indexPiece + 1} sur {ordrePieces.length} · {niveauDe(activeRoom) || 'Niveau non précisé'}{activeRoom.valide ? ' · validée ✓' : ''}
                          <span className="normal-case tracking-normal font-normal"> · {ordrePieces.filter(r => r.valide).length} validée{ordrePieces.filter(r => r.valide).length > 1 ? 's' : ''}</span>
                        </p>
                        <p className="text-sm text-slate-600">Vérifiez et complétez les postes de cette pièce, puis validez : la suivante s'ouvre.</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <select value={activeRoom.id} onChange={e => setActiveRoomId(Number(e.target.value) || e.target.value)} title="Aller à une pièce"
                                className="select-compact px-2 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 outline-none max-w-[14rem]">
                          {regrouperParNiveau(rooms).map(g => (
                            <optgroup key={g.niveau} label={g.niveau}>
                              {g.rooms.map(r => <option key={r.id} value={r.id}>{r.valide ? '✓ ' : ''}{r.customLabel || r.label}</option>)}
                            </optgroup>
                          ))}
                        </select>
                        <button onClick={() => allerPiece(-1)} disabled={indexPiece <= 0}
                                className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 disabled:opacity-40">← Précédente</button>
                        <button onClick={() => validerPiece()} className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold">
                          {indexPiece >= ordrePieces.length - 1 ? 'Valider et passer au style →' : 'Valider la pièce →'}
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={indexPiece + 1} aria-valuemin={1} aria-valuemax={ordrePieces.length}>
                      <div className="h-full rounded-full bg-orange-500 transition-[width] duration-300" style={{ width: `${((indexPiece + 1) / Math.max(1, ordrePieces.length)) * 100}%` }} />
                    </div>
                    {propagation && <p className="mt-2 text-xs text-emerald-700" aria-live="polite">{propagation.texte}</p>}
                    {preparation && (preparation.retirees.length > 0 || preparation.exclus.length > 0 || preparation.reseaux) && (
                      <details className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        <summary className="cursor-pointer font-semibold text-slate-700">
                          Préparé automatiquement : {[preparation.reseaux && 'lots techniques par logement', preparation.retirees.length && `${preparation.retirees.length} doublon${preparation.retirees.length > 1 ? 's' : ''} retiré${preparation.retirees.length > 1 ? 's' : ''}`, preparation.exclus.length && `${preparation.exclus.length} poste${preparation.exclus.length > 1 ? 's' : ''} Enedis hors devis`].filter(Boolean).join(' · ')}
                        </summary>
                        <ul className="mt-2 space-y-0.5 list-disc pl-4">
                          {preparation.exclus.map((x, i) => <li key={`e${i}`}>Hors devis, en sus : {x}</li>)}
                          {preparation.retirees.map((x, i) => <li key={`r${i}`}>{x}</li>)}
                        </ul>
                        <button type="button" onClick={() => setPreparation(null)} className="mt-2 text-[11px] font-semibold underline">Masquer</button>
                      </details>
                    )}
                    {aAssumer === activeRoom.id && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 flex flex-wrap items-center gap-2" role="alert">
                        <span className="min-w-0 flex-1">Dimensions provisoires (pas de cote relevée) : les quantités au m² sont estimées. Saisissez les cotes, ou assumez l'estimation — le dossier client la signalera « à relever ».</span>
                        <button type="button" onClick={() => { setAAssumer(null); setPlanOuvert(true); }} className="px-3 py-1.5 rounded-lg border border-amber-300 bg-white font-semibold">Saisir les cotes</button>
                        <button type="button" onClick={() => validerPiece(true)} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold">Assumer et valider</button>
                      </div>
                    )}
                  </div>
                  {/* La pièce telle qu'on la voit : ses photos, son rendu, son plan — sans quitter l'écran. */}
                  <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-2 overflow-x-auto">
                      {(activeRoom.photos || []).filter(p => p && (p.url || p.src)).slice(0, 8).map(p => (
                        <a key={p.id || p.src} href={p.url || p.src} target="_blank" rel="noreferrer" className="shrink-0">
                          <img src={p.url || p.src} alt={p.caption || ''} className="h-20 w-28 object-cover rounded-lg border border-slate-200" />
                        </a>
                      ))}
                      {activeRoom.afterPhoto?.url && (
                        <a href={activeRoom.afterPhoto.url} target="_blank" rel="noreferrer" className="shrink-0 relative">
                          <img src={activeRoom.afterPhoto.url} alt="Rendu après" className="h-20 w-28 object-cover rounded-lg border-2 border-orange-300" />
                          <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-orange-600 text-white rounded px-1">après</span>
                        </a>
                      )}
                      {!(activeRoom.photos || []).length && !activeRoom.technique && <span className="text-xs text-slate-400 px-1">Aucune photo pour cette pièce.</span>}
                      {!activeRoom.technique && projectPhotos.filter(p => p && (p.url || p.src)).length > 0 && (
                        <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200 shrink-0">
                          <span className="text-[10px] text-slate-400 leading-tight w-16">Photos non rangées : toucher pour ajouter ici</span>
                          {projectPhotos.filter(p => p && (p.url || p.src)).slice(0, 6).map(p => (
                            <button key={p.id || p.src} type="button" title="Ranger dans cette pièce"
                                    onClick={() => { marquerRetouche('photo rangée'); updateRoom(activeRoom.id, { photos: [...(activeRoom.photos || []), p] }); setProjectPhotos(prev => prev.filter(x => x !== p)); }}
                                    className="shrink-0 relative opacity-70 hover:opacity-100">
                              <img src={p.url || p.src} alt={p.caption || ''} className="h-14 w-20 object-cover rounded-md border border-dashed border-slate-300" />
                              <span className="absolute top-0.5 right-0.5 text-[10px] font-bold bg-white rounded px-1">+</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <button type="button" onClick={() => setVueNiveaux(v => !v)} className={`${activeRoom.technique ? 'ml-auto ' : 'ml-auto '}shrink-0 px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50`}>
                        {vueNiveaux ? 'Masquer les niveaux' : 'Tous les niveaux'}
                      </button>
                      {!activeRoom.technique && (
                        <button type="button" onClick={() => setPlanOuvert(true)} className="shrink-0 px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50">
                          Plan &amp; cotes{estPieceProvisoire(activeRoom) && !activeRoom.provisoireAssume ? ' · à relever' : ''}
                        </button>
                      )}
                    </div>
                  </div>
                  <LogementsReseaux logements={logements} onChange={(l) => { marquerRetouche('logements'); setLogements(l); }}
                    modeChauffage={modeChauffage} onModeChauffage={(m) => { marquerRetouche('chauffage'); setModeChauffage(m); }}
                    onProposer={proposerLogements} onGenerer={genererReseaux} resume={resumeReseaux}
                    doublons={resumeReseaux ? doublonsReseaux : 0} onRetirerDoublons={retirerDoublonsReseaux}
                    ouvert={reseauxOuvert} onToggle={() => setReseauxOuvert(o => !o)} />
                  {/* Ajouter un poste : un champ, les résultats dessous — rien d'autre */}
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-2">
                      <input
                        type="search"
                        value={itemSearch}
                        onChange={e => setItemSearch(e.target.value)}
                        placeholder="Rechercher un poste…"
                        className="flex-1 px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:border-orange-500 outline-none"
                      />
                      <button onClick={() => addCustomAddon(activeRoom.id)} title="Poste en texte libre, prix à saisir"
                              className="shrink-0 px-3 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-semibold text-slate-600">+ Texte libre</button>
                    </div>
                    {itemSearch.trim() && (
                      <div className="space-y-1 max-h-72 overflow-y-auto mt-3">
                        {filteredItems.length > 0 ? filteredItems.map(item => {
                          const alreadyAdded = activeRoom.lines.some(l => l.key === item.key);
                          return (
                            <button key={item.key}
                              onClick={() => { if (!alreadyAdded) addLine(activeRoom.id, item.key); }}
                              disabled={alreadyAdded}
                              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between gap-3 transition-colors ${
                                alreadyAdded ? 'bg-green-50 text-green-700' : 'hover:bg-orange-50 active:bg-orange-100'
                              }`}>
                              <span className={`min-w-0 ${alreadyAdded ? 'text-green-600' : 'text-slate-700'}`}>
                                {alreadyAdded && '✓ '}{item.label}
                                <span className="text-slate-400 text-xs"> · {CAT_LABELS[item.category] || item.category}</span>
                              </span>
                              <span className="text-xs text-slate-400 whitespace-nowrap">{effectivePrice(item, positionSelonEtat(BASES, item.key, item.unit, pos)).toFixed(0)} €/{item.unit}</span>
                            </button>
                          );
                        }) : <p className="text-sm text-slate-400 text-center py-3">Aucun poste trouvé</p>}
                      </div>
                    )}
                  </div>

                  {/* Added lines */}
                  {activeRoom.lines.length > 0 && (
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                      <h3 className="font-bold text-slate-900 mb-3">Postes ajoutés ({activeRoom.lines.length})</h3>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {activeRoom.lines.map((line, idx) => {
                          const item = resolveItem(pricing, line.key);
                          const computed = estimate.rooms.find(r => r.id === activeRoom.id)?.lines.find(l => l.idx === idx);
                          if (!item) return null;
                          // Le PU est celui du poste dans tout le devis ; forcé ici ou dans une autre pièce, il vaut partout.
                          const force = prixForce.has(line.key);
                          const forceIci = line.priceOverride != null;
                          const recouvrement = RECOUVREMENTS[line.key];
                          const doublons = recouvrement ? activeRoom.lines.filter(l => recouvrement.couvre.includes(l.key)).map(l => resolveItem(pricing, l.key)?.label).filter(Boolean) : [];
                          return (
                            <div key={idx} className="bg-slate-50 rounded-lg p-2.5 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-slate-800 leading-snug">{item.label}</div>
                                  <div className="text-[11px] text-slate-400 mt-0.5">{line.note || item.unit}{line.aConfirmer && <span className="ml-1.5 rounded bg-amber-100 text-amber-800 px-1 font-semibold">à confirmer</span>}</div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="font-bold text-sm text-slate-900 tabular-nums">{fmt(computed?.total || 0)} €</div>
                                  <button onClick={() => removeLine(activeRoom.id, idx)} className="text-[11px] text-slate-400 hover:text-red-500" aria-label="Supprimer">retirer</button>
                                </div>
                              </div>
                              {doublons.length > 0 && (
                                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                                  Recouvre déjà dans cette pièce : {doublons.join(', ')} — la préparation serait comptée deux fois.
                                  <button type="button" onClick={() => updateLine(activeRoom.id, idx, { key: recouvrement.finition, priceOverride: null })}
                                          className="ml-2 font-semibold underline hover:text-amber-700">Ramener à la finition seule</button>
                                </div>
                              )}
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2">
                                {/* Le champ montre la quantité retenue, même calculée sur la pièce
                                    (qty 1 = « auto ») : taper une valeur la fige, « ↺ auto » revient
                                    à la géométrie. */}
                                {(() => {
                                  const auto = !!computed && computed.qty !== line.qty;
                                  const surface = ['m²', 'm2', 'ml'].includes(item.unit);
                                  return (
                                    <label className="flex items-center gap-1 text-[11px] text-slate-500">Qté
                                      <input type="number" step="0.1" min="0" value={auto ? Number(computed.qty.toFixed(2)) : line.qty}
                                        onChange={e => updateLine(activeRoom.id, idx, { qty: parseFloat(e.target.value) || 0 })}
                                        className={`w-20 px-2 py-1 border rounded text-center text-sm font-bold ${auto ? 'border-emerald-200 bg-emerald-50/60 text-emerald-900' : 'border-slate-200 bg-white'}`}
                                        title={auto ? 'Quantité calculée sur la géométrie de la pièce' : 'Quantité saisie'} />
                                      <span>{item.unit}</span>
                                      {auto
                                        ? <span className="text-[10px] font-semibold text-emerald-700" title="Calculée sur la pièce">auto</span>
                                        : (surface && <button type="button" onClick={() => updateLine(activeRoom.id, idx, { qty: 1 })} className="text-[10px] text-slate-400 hover:text-orange-600" title="Revenir à la quantité calculée sur la pièce">↺ auto</button>)}
                                    </label>
                                  );
                                })()}
                                <label className="flex items-center gap-1 text-[11px] text-slate-500">PU
                                  <input type="number" step="0.5" min="0" value={forceIci ? line.priceOverride : Number((computed?.unitPrice ?? 0).toFixed(0))}
                                    onChange={e => updateLine(activeRoom.id, idx, { priceOverride: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                    className={`w-20 px-2 py-1 border rounded text-center text-sm font-bold ${forceIci ? 'border-orange-300 bg-orange-50 text-orange-700' : force ? 'border-orange-200 border-dashed bg-orange-50/40 text-orange-800' : 'border-slate-200 bg-white'}`}
                                    title={forceIci ? 'Prix forcé — vaut pour ce poste dans tout le devis' : force ? 'Prix forcé sur ce poste dans une autre pièce — le même partout' : 'Prix de la grille selon l’état du bien — le même dans tout le devis'} />
                                  <span>€/{item.unit}</span>
                                  {forceIci && <button type="button" onClick={() => updateLine(activeRoom.id, idx, { priceOverride: null })} className="text-[10px] text-slate-400 hover:text-orange-600" title="Revenir au prix de la grille">↺</button>}
                                </label>
                                {force && <span className="text-[11px] text-orange-700" title="Un PU forcé vaut pour ce poste dans tout le devis.">PU forcé · tout le devis</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Suggestions */}
                  {suggestions.length > 0 && (
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <h3 className="font-bold text-blue-900 mb-3 text-sm">Suggestions ({suggestions.length})</h3>
                      <div className="space-y-2">
                        {suggestions.map(s => (
                          <div key={s.key} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-blue-100">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-blue-900">{s.label}</div>
                              <div className="text-xs text-blue-600 mt-0.5">{s.reason}</div>
                            </div>
                            <button onClick={() => addLine(activeRoom.id, s.key)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shrink-0">+ Ajouter</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Postes en texte libre déjà saisis */}
                  {activeRoom.addons.length > 0 && (
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <h3 className="font-bold text-slate-900 mb-3 text-sm">Postes en texte libre</h3>
                      <div className="space-y-2 mt-4 pt-3 border-t border-slate-100">
                        {activeRoom.addons.map((addon, idx) => {
                          const computed = estimate.rooms.find(r => r.id === activeRoom.id)?.addons.find(a => a.idx === idx);
                          return (
                            <div key={idx} className="flex items-center gap-3 bg-amber-50 rounded-lg p-3">
                              <input type="text" value={addon.label} onChange={e => updateAddon(activeRoom.id, idx, { label: e.target.value })} placeholder="Libellé" className="flex-1 px-2 py-1.5 border border-amber-200 rounded text-sm bg-white" />
                              <select value={addon.unit} onChange={e => updateAddon(activeRoom.id, idx, { unit: e.target.value })} className="px-2 py-1.5 border border-amber-200 rounded text-sm bg-white">
                                <option value="m²">m²</option><option value="ml">ml</option><option value="u">u</option><option value="forfait">forfait</option>
                              </select>
                              <input type="number" step="0.1" value={addon.qty} onChange={e => updateAddon(activeRoom.id, idx, { qty: parseFloat(e.target.value) || 0 })} className="w-16 px-2 py-1.5 border border-amber-200 rounded text-center text-sm font-bold bg-white" />
                              <input type="number" step="0.5" value={addon.price} onChange={e => updateAddon(activeRoom.id, idx, { price: parseFloat(e.target.value) || 0 })} className="w-20 px-2 py-1.5 border border-amber-200 rounded text-center text-sm font-bold bg-white" />
                              <div className="w-24 text-right font-bold text-sm text-amber-700">{fmt(computed?.total || 0)} €</div>
                              <button onClick={() => removeAddon(activeRoom.id, idx)} className="text-amber-300 hover:text-red-500 p-1">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                  </div>
                  )}

                </>
              )}

              {/* ═══ ÉTAPE 6 : Rendu avant/après, pièce par pièce ═══ */}
              {step === 6 && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                    <span className="text-sm font-bold text-slate-900">🪄 Retoucher un rendu — {activeRoom.customLabel || activeRoom.label}</span>
                    <select value={activeRoom.id} onChange={e => setActiveRoomId(Number(e.target.value) || e.target.value)} title="Choisir la pièce"
                            className="select-compact px-2 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 outline-none max-w-[16rem]">
                      {regrouperParNiveau(rooms).map(g => (
                        <optgroup key={g.niveau} label={g.niveau}>
                          {g.rooms.map(r => <option key={r.id} value={r.id}>{r.afterPhoto ? '🖼️ ' : ''}{r.customLabel || r.label}{(r.photos || []).some(ph => ph?.id) ? '' : ' (sans photo)'}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-slate-400 mb-4">
                    La palette choisie ci-dessus est appliquée aux <strong>surfaces réellement travaillées</strong> de cette pièce.
                  </p>
                  <AfterStudio
                    key={activeRoom.id}
                    leadRef={ref}
                    room={activeRoom}
                    style={style}
                    clientBrief={clientBrief}
                    onChange={(rendu) => poserRendu(activeRoom.id, rendu)}
                    onBriefChange={(aiBrief) => updateRoom(activeRoom.id, { aiBrief })}
                  />
                </div>
              )}
            </>
          )}
        </div>
        )}

      {/* ════════ ÉTAPE 7 — RÉCAP & ENVOI ════════ */}
      {step === 7 && (
        <div className="space-y-4">
          {/* Lire le devis : de quoi l'expliquer au client */}
          {rooms.length > 0 && (() => {
            const repere = lecture.prixM2 ? repereDe(lecture.prixM2) : null;
            const totalParts = lecture.parts.supports + lecture.parts.reseaux + lecture.parts.equipements || 1;
            const pct = (v) => `${Math.round((v / totalParts) * 100)} %`;
            return (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-lg font-black text-slate-900 mb-1">Lire le devis</h2>
                <p className="text-sm text-slate-400 mb-4">Les chiffres qui expliquent la proposition au client.</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  <Kpi label="Surface rénovée" value={`${lecture.surface.toFixed(0)} m²`}
                       sub={`${lecture.nbMesurees} pièce${lecture.nbMesurees > 1 ? 's' : ''} cotée${lecture.nbMesurees > 1 ? 's' : ''}${lecture.nbProvisoires ? ` · ${lecture.nbProvisoires} sans cote, hors surface` : ''}`} />
                  <Kpi label="Prix HT au m²" value={lecture.prixM2 ? `${fmt(lecture.prixM2)} €` : '—'}
                       sub={repere ? `${repere.label} (repère ${repere.plage})` : 'aucune pièce cotée'} />
                  <Kpi label="Prix TTC au m²" value={lecture.prixM2TTC ? `${fmt(lecture.prixM2TTC)} €` : '—'} sub={`TVA ${(tvaRate * 100).toFixed(1).replace('.0', '')} %`} />
                  <Kpi label="Total TTC" value={`${fmt(estimate.ttc)} €`} sub={estimate.coordination > 0 ? `${fmt(estimate.htTotal)} € HT dont ${fmt(estimate.coordination)} € de coordination` : `${fmt(estimate.htTotal)} € HT`} accent />
                </div>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Par corps d'état</p>
                    <Barres total={estimate.ht} lignes={lecture.parLot.map(x => ({ label: x.label === '_extras' ? 'Prestations complémentaires' : (CAT_LABELS[x.label] || x.label), value: x.value }))} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Par étage</p>
                    <Barres total={estimate.ht} lignes={lecture.parNiveau} />
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-6 mt-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Les cinq postes les plus lourds</p>
                    <ol className="space-y-1.5">
                      {lecture.top.map((l, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="min-w-0 truncate text-slate-700">{l.label} <span className="text-slate-400 text-xs">· {l.piece}</span></span>
                          <span className="tabular-nums font-semibold text-slate-900 whitespace-nowrap">{fmt(l.total)} € <span className="text-slate-400 font-normal text-xs">· {estimate.ht ? Math.round((l.total / estimate.ht) * 100) : 0} %</span></span>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">À dire au client</p>
                    <ul className="space-y-1.5 text-sm text-slate-700">
                      <li>Le bien est estimé <strong>{(stateMultiplier[state]?.label || state).toLowerCase()}</strong> : les supports (murs, plafonds, sols) sont chiffrés en conséquence, les équipements à leur prix courant.</li>
                      <li><strong>{pct(lecture.parts.supports)}</strong> du montant va aux supports (préparation, peinture, sols), <strong>{pct(lecture.parts.reseaux)}</strong> aux réseaux (plomberie, électricité, chauffage), <strong>{pct(lecture.parts.equipements)}</strong> aux équipements et menuiseries.</li>
                      <li>{lecture.nbLignes} poste{lecture.nbLignes > 1 ? 's' : ''} sur {rooms.length} pièce{rooms.length > 1 ? 's' : ''}{estimate.coordination > 0 ? `, coordination de chantier incluse (${Math.round(coordRate * 100)} %, ${fmt(estimate.coordination)} €)` : ''} : un seul interlocuteur.</li>
                      {lecture.nbProvisoires > 0 && <li className="text-amber-800">{lecture.nbProvisoires} pièce{lecture.nbProvisoires > 1 ? 's' : ''} sans cote ({fmt(lecture.parNiveau.filter(x => /non précisé|Ensemble/.test(x.label)).reduce((s, x) => s + x.value, 0))} €) : à relever avant devis ferme.</li>}
                      <li>{lecture.rendus ? `${lecture.rendus} rendu${lecture.rendus > 1 ? 's' : ''} « après » à montrer` : 'Aucun rendu « après » encore — l’étape Style les produit'}. Estimation valable 30 jours, montant définitif au devis contractuel.</li>
                    </ul>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Récapitulatif par pièce */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-black text-slate-900">Récapitulatif</h2>
              <span className="text-xs text-slate-500">{stateMultiplier[state]?.label} · {TVA_TAUX[tvaKey]?.label} · <button type="button" onClick={() => setStep(1)} className="underline">modifier</button></span>
            </div>
            {rooms.length === 0 ? (
              <p className="text-sm text-slate-400">Aucune pièce. Reviens à l'étape Pièces pour en ajouter.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-xs">
                    <th className="text-left py-2 font-semibold">Pièce</th>
                    <th className="text-right py-2 font-semibold">Surface</th>
                    <th className="text-right py-2 font-semibold">Postes</th>
                    <th className="text-right py-2 font-semibold">Avant/Après</th>
                    <th className="text-right py-2 font-semibold">Sous-total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {estimate.rooms.map((re, i) => {
                    const r = rooms[i];
                    return (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-2 font-semibold text-slate-800">{re.label}</td>
                        <td className="py-2 text-right text-slate-500">{re.surface.toFixed(1)} m²</td>
                        <td className="py-2 text-right text-slate-500">{re.lines.length + (re.addons?.length || 0)}</td>
                        <td className="py-2 text-right">{r?.afterPhoto ? '✅' : '—'}</td>
                        <td className="py-2 text-right font-bold text-orange-600">{fmt(re.total)} €</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Documents & envoi */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
            <h3 className="font-bold text-slate-900 mb-3">Documents & envoi</h3>
            <div className="flex flex-wrap gap-3">
              <button onClick={openDossier} disabled={!hasContent}
                className="px-5 py-3 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm transition-colors">
                ✨ Dossier de présentation
              </button>
              {/* Email client */}
              {sentClient ? (
                <div className="px-5 py-3 bg-green-700 text-white rounded-xl font-bold text-sm flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Email envoyé au client
                </div>
              ) : (
                <button onClick={sendToClient} disabled={sendingClient || !client.email || !hasContent}
                  className="px-5 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm transition-colors">
                  {sendingClient ? 'Envoi...' : `Envoyer au client (${client.email || 'email manquant'})`}
                </button>
              )}
            </div>
            {!client.email && !sentClient && (
              <p className="text-xs text-slate-400 mt-3">Renseigne l'email du client (étape 1) pour l'envoi direct.</p>
            )}
            {sendError && <p className="text-red-500 text-sm mt-3">{sendError}</p>}
          </div>
        </div>
      )}

      {/* ──── Planification équipe, une fois le devis envoyé ──── */}
      {step === 7 && sentClient && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 mt-6 mb-32">
          <h2 className="font-black text-lg text-slate-900 mb-4">Planification équipe</h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Nb ouvriers</label>
              <input type="number" min="1" max="10" value={nbOuvriers} onChange={e => setNbOuvriers(parseInt(e.target.value) || 2)}
                className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm font-bold text-center focus:border-orange-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Urgence</label>
              <select value={urgence} onChange={e => setUrgence(e.target.value)} className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none bg-white">
                <option value="basse">Basse — flexible</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent — prioritaire</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Début souhaité</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Fin estimée</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" />
            </div>
          </div>

          <button
            onClick={async () => {
              const lineCategories = [...new Set(rooms.flatMap(r => r.lines.map(l => l.key.split('.')[0])))];
              try {
                const res = await proFetch('/suggest-team', {
                  method: 'POST',
                  body: JSON.stringify({ lineCategories, startDate, endDate, nbOuvriers, urgence }),
                });
                setTeamSuggestions(await res.json());
              } catch (err) { setSendError(err.message); }
            }}
            disabled={!startDate}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-bold rounded-xl text-sm mb-4"
          >
            Suggérer une équipe
          </button>

          {teamSuggestions && (
            <div>
              <div className="text-xs text-slate-500 mb-3">
                Skills requis : {teamSuggestions.requiredSkills?.map(s => (
                  <span key={s} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded mr-1 font-semibold">{s}</span>
                ))}
              </div>

              <div className="grid gap-2">
                {teamSuggestions.suggestions?.map((s, i) => (
                  <div key={s._id} className={`flex items-center gap-4 p-3 rounded-lg border ${i < nbOuvriers ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-100'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < nbOuvriers ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-sm text-slate-900">{s.prenom} {s.nom}</div>
                      <div className="flex gap-1 mt-1">
                        {s.matchedSkills.map(sk => (
                          <span key={sk} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">{sk}</span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-sm font-bold ${s.availability >= 80 ? 'text-green-600' : s.availability >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {s.availability}% dispo
                      </div>
                      <div className="text-xs text-slate-400">{s.freeDays}/{s.totalDays} jours libres</div>
                      {s.coutHoraire > 0 && <div className="text-xs text-slate-400">{s.coutHoraire} €/h</div>}
                    </div>
                    {i < nbOuvriers && startDate && endDate && (
                      <button
                        onClick={async () => {
                          // Assign worker to project for the date range
                          const entries = [];
                          const d = new Date(startDate);
                          const end = new Date(endDate);
                          const label = `${client.prenom} ${client.nom}`.trim();
                          while (d <= end) {
                            const dow = d.getDay();
                            if (dow !== 0 && dow !== 6) { // skip weekends
                              entries.push({
                                workerId: s._id,
                                date: d.toISOString().split('T')[0],
                                type: 'chantier',
                                projectRef: ref || '',
                                label,
                                heures: 8,
                              });
                            }
                            d.setDate(d.getDate() + 1);
                          }
                          await proFetch('/planning/bulk', { method: 'POST', body: JSON.stringify({ entries }) });
                          alert(`${s.prenom} affecté du ${startDate} au ${endDate}`);
                        }}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold shrink-0"
                      >
                        Affecter
                      </button>
                    )}
                  </div>
                ))}
                {teamSuggestions.suggestions?.length === 0 && (
                  <div className="text-sm text-slate-400 text-center py-4">Aucun ouvrier avec les skills requis</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──── BOTTOM BAR: Totals + Actions ──── */}
      {rooms.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-slate-900 text-white px-5 py-2 shadow-2xl z-30 safe-bottom">
          <div className="max-w-6xl flex items-center justify-between gap-4">
            <div className="flex gap-6 items-baseline">
              <div>
                <div className="text-[10px] text-slate-400 leading-none">Total HT</div>
                <div className="text-sm font-bold">{fmt(estimate.htTotal)} €</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-400 leading-none">TVA ({(tvaRate * 100).toFixed(1)}%)</div>
                <div className="text-sm font-bold">{fmt(estimate.tva)} €</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-400 leading-none">Total TTC</div>
                <div className="text-lg font-black text-orange-500">{fmt(estimate.ttc)} €</div>
              </div>
              {estimate.coordination > 0 && (
                <div>
                  <div className="text-[10px] text-slate-400 leading-none">dont coordination ({Math.round(coordRate * 100)}%)</div>
                  <div className="text-xs text-slate-300">{fmt(estimate.coordination)} €</div>
                </div>
              )}
              {ref && (
                <div>
                  <div className="text-[10px] text-slate-400 leading-none">Relevé</div>
                  {saveState === 'error' ? (
                    <div className="text-sm font-bold text-red-400">⚠️ Non enregistré</div>
                  ) : saveState === 'saving' ? (
                    <div className="text-sm font-bold text-slate-300">Enregistrement…</div>
                  ) : lastSavedAt ? (
                    <div className="text-sm font-bold text-green-400">✓ {lastSavedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                  ) : (
                    <div className="text-sm text-slate-500">—</div>
                  )}
                </div>
              )}
            </div>
            {/* Navigation du wizard */}
            <div className="flex gap-3">
              {step > 1 && (
                <button onClick={() => setStep(s => (s === 5 ? 3 : Math.max(1, s - 1)))}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg font-bold text-sm transition-colors">
                  &larr; Précédent
                </button>
              )}
              {step < DERNIERE && (
                <button onClick={() => setStep(s => (s === 3 ? 5 : Math.min(DERNIERE, s + 1)))}
                  className="px-5 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg font-bold text-sm transition-colors">
                  {WIZARD_STEPS.find(w => w.n === (step === 3 ? 5 : step + 1))?.label} &rarr;
                </button>
              )}
            </div>
          </div>
          {sendError && <p className="text-red-400 text-sm mt-2 max-w-6xl">{sendError}</p>}
        </div>
      )}
    </Page>
  );
}
